/*
// this is how to use the deliverableTemplate namespaces within the app for the components to open more than one deliverableTemplate.
// For example: opening a social media plan, then opening a social media post within that plan while still keeping the plan open.
// you will need to make sure you have two different namespaces
// pass "" value to namespace prop to use the default store.
// pass "relatedDeliverable" value to use a copy of the default store.
// import { mapGetters, mapState, mapActions } from "vuex";
{
	props: ['namespace'],
  computed: {
  ...mapState({
    state (state) {
      return state[this.namespace]
    },
    someGetter (state, getters) {
      return getters[this.namespace + 'someGetter']
    },
  }),
  methods: {
    ...mapActions({
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
    }),

    ...mapMutations({
      someMutation (commit, payload) {
        return commit(this.namespace + 'someMutation', payload)
      },
    }),

    ...mapActions({
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
    }),

  }
}
*/
require("es6-promise").polyfill();
import axios from "axios";
import Vue from "vue";
import router from "../../routes";
import toast from "../../toast";

import { sortTaskFields, sortTasks } from "../../mixin";

import axiosRetry from 'axios-retry';

axiosRetry(axios, {
    retries: 3,
    retryDelay: (retryCount) => {
        return retryCount * 1000;
    },
    retryCondition: (error) => {
        if(error.message && error.message == 'Network Error') return true
    }
});

axios.interceptors.response.use(
    response => {
        return response;
    },
    error => {
        if (!error.response) {
            // network error
            toast.error(
                "Request could not be completed at the moment. There is something wrong with your network connection."
            );
            return new Promise((resolve, reject) => {
                reject(error);
            });
        } else {
            const code = error.response.status;
            const response = error.response.data;
            const message = error.message;
            const headers = error.response.headers;

            if (code === 404) {
                const silent404 = error.config && (error.config.silent404 || error.config.silent === true);
                if (silent404) {
                    return new Promise((resolve, reject) => {
                        reject(error);
                    });
                }
                if (error.response.data.message) {
                    toast.error(error.response.data.message);
                } else {
                    toast.error(message);
                }

                return new Promise((resolve, reject) => {
                    reject(error);
                });
            }
            else if (code === 422) {
                const contentType = headers['content-type'];
                const errorMessages = error.response.data.errors;
                if (errorMessages) {
                    toast.error("Request could not be completed.");
                } else if (contentType.includes('application/json')) {
                    // If it's JSON within Blob, handle it as a JSON response
                    const reader = new FileReader();
                    reader.onload = () => {
                        const jsonResponse = JSON.parse(reader.result);
                        toast.error(jsonResponse.message);
                    };
                    reader.readAsText(error.response.data);
                } 
                return new Promise((resolve, reject) => {
                    reject(error.response.data);
                });
            }
            else if (code === 423) {
                // 423 Locked - Project is being duplicated
                const data = error.response.data;
                
                // Show toast message
                toast.error(data.message || 'This project is currently locked.');
                
                return new Promise((resolve, reject) => {
                    reject(error);
                });
            }
            else if (code === 401) {
                if(typeof response.message != 'undefined')
                    toast.error(response.message);
                else
                    toast.error('You do not have permission to perform this action.');
                return new Promise((resolve, reject) => {
                    reject(error);
                });
            }
            else {
                // All other error codes
                toast.error(message);
                return new Promise((resolve, reject) => {
                    reject(error);
                });
            }
        }
    }
);

const defaultFilterParams = {
    query: "",
    sort: '',
    multiSort: [],
    priorityFilter: 0,
    outletCompany: [],
    filterByDate: false,
    filterByDateCreated: false,
    filterByDateCompleted: false,
    filterByPublishDate: false,
    dateRange: {
        startDate: new Date(),
        endDate: new Date()
    },
    dateCreatedRange: {
        startDate: new Date(),
        endDate: new Date()
    },
    dateCompletedRange: {
        startDate: new Date(),
        endDate: new Date()
    },
    datePublishRange: {
        startDate: new Date(),
        endDate: new Date()
    },
    status: [],
    publishStatus: [],
    outlet: [],
    deliverableTemplate: [],
    tag: [],
    department: [],
    coordinator: [],
    project: [],
    noDeparment: 'not_accepted',
    noOutlet: 'not_accepted',
};
const state = () => ({
	showPostContentLangInListing:1,
	showPostContentNetworkInListing:1,
	openWithFocus:null,
	refreshKey:0,
	isDirty:false,
	componentState:localStorage.getItem('componentState') ? JSON.parse(localStorage.getItem('componentState')) : [],
	activeTab:localStorage.getItem('activeTab') ? JSON.parse(localStorage.getItem('activeTab')) : [],
	_namespace: "",
	_namespaceKey: "",
    storedSortFieldId: localStorage.getItem('storedSortFieldId'),
    storedSortKey: localStorage.getItem('storedSortKey'),
    nextSortState: localStorage.getItem('nextSortState'),
    storedSortState: localStorage.getItem('storedSortState'),
    storedTaskFieldsOrder: [{1: [14,12,4,2,13]}],
    storedTaskOrder: null,
    forceUpdateCounter: 0,
    preferenceData: null,
    loggedInUserData: null,
    auditLogs: [],
    auditLogTags: [],
    auditLogContacts: [],
    auditLogDepartments: [],
    auditLogCoordinators: [],
    auditLogDeliverableTemplates: [],
    auditLogProjects: [],
    activateUndo: false,
    activateUndoText: '',
    activateUndoNoScroll: false,
    activateMyProjectUndo: false,
    activateMyProjectUndoId: '',
    breadcrumbs: {},
    newNotificationPopupClose: false,
    userViews: [],
    GSRecentSearch: {
        all: [],
        tags: [],
        departments: [],
        contacts: [],
        coordinators: []
    },
    allRoles: [],
    deliverableTemplateRoles: [],
    allAbilities: [],
    allCompanies: [],
    initialContacts: [],
    initialTags: [],
    initialDepartments: [],
    globalSearchData: {
        tags: {
            total: false
        },
        departments: {
            total: false
        },
        coordinators: {
            total: false
        },
        contacts: {
            total: false
        }
    },
    currentPage: false,
    deliverableTemplates: [],
    newDeliverableTemplates: [],
    mergeDeliverableTemplates: [],
    projectDeliverableTemplates: [],
    calendarDeliverableTemplates: [],
    yearCalendarDeliverableTemplates: [],
    settings:null,
    projects: [],
    userProjects: [],
    userManageProjects: [],
    userManageSocialCampaigns: [],
    allProjects: [],
    userHasProjects: false,
    defaultFilterParams: defaultFilterParams,
    mediaCallFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        outlet: [],
        deliverableTemplate: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    dashboardMediaCallsFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        outlet: [],
        deliverableTemplate: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        },
        dashboardType: '',
    },
    globalSearchFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        outlet: [],
        deliverableTemplate: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    auditLogFilter: {
        query: "",
        filterByDateCreated: false,
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        coordinator: []
    },
    allDeliverableTemplatesPaginationData: {},
    allUserProjectsPaginationData: {},
    userDeliverableTemplatesPaginationData: {},
    userDeliverableTemplates: {
        overdue: [],
        today: [],
        thisWeek: [],
        nextWeek: [],
        upcoming: [],
        noDeadline: [],
        projectOnHold: [],
        completed: []
    },
    contacts: [],
    activeContacts: [],
    users: [],
    activeUsers: [],
    projectDetail: [],
    departments: [],
    activeDepartments: [],
    subjects: [],
    tags: [],
    trendingTags: [],
    editDeliverableModal: false,
    editProjectModal: false,
    addContactModal: false,
    editContactModal: false,
    editSocialModal: false,
    newContactName: "",
    deliverableTemplateId: "",
    countries: [],
    regions: [],
    countryStates: [],
    currentContactId: "",
    currentContactData: {},
    currentFieldKey: "",
    currentContactTemplate: "",
    customFields: [],
    editingField: false,
    unGroupedUserDeliverableTemplates: [],
    outletCount: 0,
    userAlerts: {},
    allAlerts: {},
    notification: {},
    allDeliverableTypes: [],
    allDeliverableTemplates: [],
    deliverableTemplates: [],
    deliverableTemplateDetail: [],
    newDeliverableTypes:[],
    preferenceDeliverableTypes: [],
    calendarEvents: [],
    projectCalendarEvents: [],
    dataLoading: false,
    deliverableTemplateCount: {},
    tasksToHighlight: [],
    taskHighlighted: null,
    taskHighlightTimeout: null,
    newProjectCreated: false,
    projectSort: '',
    myProjectSort: '',
    numberStat: {},
    widgetSettings: {},
    systemSettings: {},
    listSocialSeries: [],
    listSocialCampaigns: [],

    deliverableTemplateFilter: {
      query: "",
      sort: '',
      multiSort: [],
      priorityFilter: 0,
      parentDeliverableId: 0,
      outletCompany: [],
      filterByDate: false,
      filterByDateCreated: false,
      filterByDateCompleted: false,
      filterByPublishDate: false,
      dateRange: {
          startDate: new Date(),
          endDate: new Date()
      },
      dateCreatedRange: {
          startDate: new Date(),
          endDate: new Date()
      },
      dateCompletedRange: {
          startDate: new Date(),
          endDate: new Date()
      },
      datePublishRange: {
          startDate: new Date(),
          endDate: new Date()
      },
      status: [],
      publishStatus: [],
      // socialNetwork: [],
      outlet: [],
      deliverableTemplate: [],
      tag: [],
      department: [],
      coordinator: [],
      project: [],
      noDeparment: 'not_accepted',
      noOutlet: 'not_accepted',
      weekFilterOptions: {
        overdue: false,
        today: false,
        thisWeek: false,
        nextWeek: false,
        upcoming: false,
        noDeadline: false,
      }
    },
    deliverableTemplateCount: {}

});

const mutations = {
	showPostContentNetworkInListing:(state, showPostContentNetworkInListing) => {
	    state.showPostContentNetworkInListing = showPostContentNetworkInListing;
	},
	showPostContentLangInListing:(state, showPostContentLangInListing) => {
	    state.showPostContentLangInListing = showPostContentLangInListing;
	},

    updateNewContactRelatedValues(state, { 
        newContactName, 
        deliverableTemplateId, 
        fieldKey, 
        currentContactTemplate 
    }) {
        state.newContactName = newContactName;
        state.deliverableTemplateId = deliverableTemplateId;
        state.currentFieldKey = fieldKey;
        state.currentContactTemplate = currentContactTemplate;
    },
    updateEditContactRelatedValues(state, { 
        currentContactData, 
        currentContactId, 
        currentFieldKey, 
        currentContactTemplate 
    }) {
        state.currentContactData = currentContactData;
        state.currentContactId = currentContactId;
        state.currentFieldKey = currentFieldKey;
        state.currentContactTemplate = currentContactTemplate;
    },

    setIsDirty:(state, isDirty) => {
        state.isDirty = isDirty;
    },

    setNumberStat:(state, numberStat) => {
        state.numberStat = numberStat;
    },
    setWidgetSettings:(state, widgetSettings) => {
        state.widgetSettings = widgetSettings;
    },
    updateDepartmentList: (state, departments) => {
        let departmentsArr = [];
        departments.forEach(function(item, index) {
            departmentsArr.push({ id: item.id, title: item.name });
        });
        state.departments = departmentsArr
    },
    resetProjectDeliverableTemplates: (state) => {
        state.projectDeliverableTemplates = []
    },
    setCalendarEvents: (state, eventsData) => {
        state.calendarEvents = eventsData
    },
    setProjectCalendarEvents: (state, eventsData) => {
        state.projectCalendarEvents = eventsData
    },
    setUserPreference: (state, preferenceData) => {
        state.preferenceData = preferenceData ? preferenceData : {deliverableTemplate_type: null};
        state.storedTaskFieldsOrder = preferenceData && typeof preferenceData.taskFieldOrder != 'undefined' ? preferenceData.taskFieldOrder : [{1: [14,12,4,2,13]}]
        state.storedTaskOrder = preferenceData && typeof preferenceData.taskOrder != 'undefined' ? preferenceData.taskOrder : null
    },
    setLoggedInUserData: (state, loggedInUserData) => {
        state.loggedInUserData = loggedInUserData;
    },
    changeForceUpdateCounter: (state) => {
        state.forceUpdateCounter += 1;
    },

     setDeliverableTemplates: (state, deliverableTemplates) => {
        state.deliverableTemplates = deliverableTemplates;
    },
    setAllDeliverableTemplates: (state, deliverableTemplates) => {
        console.log("setAllDeliverableTemplates",deliverableTemplates);
        state.allDeliverableTemplates = deliverableTemplates;
        state.newDeliverableTemplates = state.allDeliverableTemplates.filter(item => item.can_be_templated == 1);
    },
    setNewDeliverableTemplates: (state, deliverableTemplates) => {
        state.newDeliverableTemplates = deliverableTemplates;
    },
    setPreferenceDeliverableTypes: (state, deliverableTypes) => {
        state.preferenceDeliverableTypes = deliverableTypes;
    },
    setAuditLogs: (state, auditLogs) => {
        state.auditLogs = auditLogs.all;
        state.auditLogTags = auditLogs.tags;
        state.auditLogContacts = auditLogs.contacts;
        state.auditLogDepartments = auditLogs.departments;
        state.auditLogCoordinators = auditLogs.coordinators;
        state.auditLogDeliverableTemplates = auditLogs.deliverableTemplates;
        state.auditLogProjects = auditLogs.projects;
    },
    setActivateUndo: (state, status) => {
        state.activateUndo = status;
    },
    setActivateUndoText: (state, text) => {
        state.activateUndoText = text;
    },
    setActivateUndoNoScroll: (state, noScroll) => {
        state.activateUndoNoScroll = noScroll;
    },
    setActivateMyProjectUndo: (state, status) => {
        state.activateMyProjectUndo = status;
    },
    setActivateMyProjectUndoId: (state, id) => {
        state.activateMyProjectUndoId = id;
    },
    setUserAlerts: (state, alerts) => {
        state.userAlerts = alerts;
    },
    setAllAlerts: (state, alerts) => {
        state.allAlerts = alerts;
    },
    setBreadcrumbs: (state, breadcrumbs) => {
        state.breadcrumbs = breadcrumbs;
    },
    setNotification: (state, notification) => {
        state.notification = notification;
    },
    setRecentSearch: (state, {searchQuery, activeTab}) => {
        if(searchQuery != 'resetSearch') {
            if(state.GSRecentSearch[activeTab].indexOf(searchQuery.trim()) == -1) {
                setTimeout(function() {
                    if(searchQuery == state.globalSearchFilter.query) {
                        state.GSRecentSearch[activeTab].push(searchQuery.trim());
                    }
                }, 5000);
            }
        } else {
            if(activeTab == 'all') {
                state.GSRecentSearch = {
                    all: [],
                    tags: [],
                    departments: [],
                    contacts: [],
                    coordinators: []
                };
            } else {
                state.GSRecentSearch[activeTab] = [];
            }
        }
    },
    setAllCompanies: (state, data) => {
        state.allCompanies = data
    },
    setInitialData: (state, data) => {
        if(typeof data.contacts != 'undefined') {
            state.initialContacts = data.contacts;
        } else if(typeof data.tags != 'undefined') {
            state.initialTags = data.tags;
        } else if(typeof data.departments != 'undefined') {
            state.initialDepartments = data.departments;
        }
    },
    setDeliverableTemplateFilter: (state, filter) => {
        // if (router.currentRoute.name == "myWeek") {
            // state.myWeekDeliverableFilter = filter;
        // } else if(router.currentRoute.name == "dashboard") {
            state.deliverableTemplateFilter = filter;
        // } else if(router.currentRoute.name == "calendar") {
        //     state.calendarFilter = filter;
        // } else if(router.currentRoute.name == "globalSearch") {
        //     state.globalSearchFilter = filter;
        // } else if(router.currentRoute.name == "report") {
        //     state.reportFilter = filter;
        // } else if(router.currentRoute.name == "project") {
        //     state.projectFilter = filter;
        // } else if(router.currentRoute.name == "myProjects") {
        //     state.myProjectFilter = filter;
        // } else if(router.currentRoute.name == "socialMedia") {
        //     state.socialMediaFilter = filter;
        // } else if(router.currentRoute.name == "socialMediaGrouped") {
        //     state.socialMediaFilter = filter;
        // } else if(router.currentRoute.name == "socialMediaSchedule") {
        //     state.socialMediaCalendarFilter = filter;
        // }        
    },
    setDashboardMediaCallsFilter: (state, filter) => {
            state.dashboardMediaCallsFilter = filter;
    },
    setProjectCalendarFilter: (state, filter) => {
        state.projectCalendarFilter = filter;
    },
    setAuditLogFilter: (state, filter) => {
        state.auditLogFilter = filter;
    },
    setDashboardDeliverableFilter: (state, filter) => {
        Vue.set(state, 'deliverableTemplateFilter', JSON.parse(filter));
    },
    setMediaCallFilter: (state, filter) => {
        Vue.set(state, 'mediaCallFilter', JSON.parse(filter));
    },
    setMyWeekDeliverableFilter: (state, filter) => {
        Vue.set(state, 'myWeekDeliverableFilter', JSON.parse(filter));
    },
    setSocialMediaFilter: (state, filter) => {
        state.socialMediaFilter = filter;
    },
    setSocialMediaCalendarFilter: (state, filter) => {
        state.socialMediaCalendarFilter = filter;
    },
    setAddProjectDeliverableFilter: (state, filter) => {
        Vue.set(state, 'addProjectDeliverableFilter', JSON.parse(filter));
    },
    setProjectDeliverableListFilter: (state, filter) => {
        state.projectDeliverableListFilter = filter;
    },
    setProjectFilter: (state, filter) => {
        Vue.set(state, 'projectFilter', JSON.parse(filter));
    },
    setMyProjectFilter: (state, filter) => {
        Vue.set(state, 'myProjectFilter', JSON.parse(filter));
    },
    setViews: (state, views) => {
        state.userViews = views;
    },
    setDashboardFilter: (state, filter) => {
        Vue.set(state, 'deliverableTemplateFilter', filter);
    },
    setGlobalSearchData: (state, searchData) => {
        state.globalSearchData = searchData;
    },
    setOutletCount: (state, outletCount) => {
        state.outletCount = outletCount;
    },
    increaseOutletCount: state => {
        state.outletCount += 1;
    },
    increaseRefreshKey: state => {
        state.refreshKey += 1;
    },
    setProjects: (state, projects) => {
        state.projects = projects.allProjects.data;
        state.allProjectsPaginationData = projects.allProjects;
    },
    setAllProjects: (state, projects) => {
        state.allProjects = projects.allProjects;
    },
    setUserProjects: (state, projects) => {
        state.userProjects = projects.userProjects.data;
        state.allUserProjectsPaginationData = projects.userProjects;
    },
    setUserProjectsOnly: (state, projects) => {
        state.userProjects = projects;
    },
    setProjectsOnly: (state, projects) => {
        state.projects = projects;
    },
    setUserManageProjects: (state, projects) => {
        state.userManageProjects = projects.userProjects;
    },
    setUserManageSocialCampaigns: (state, socialCampaigns) => {
        state.userManageSocialCampaigns = socialCampaigns.userCampaigns;
    },
    setUserHasProjects: (state, count) => {
        if (count) {
            state.userHasProjects = count > 0;
        } else {
            state.userHasProjects = false;
        }
    },


    setProjectEngagementManagerLock: (state,responseData) => {
		if (state.projectDetail.id) {
	    	if (state.projectDetail.id != responseData.projectDetail.id) {
		    } else {
		    	state.projectDetail.engagement_manager_locked = responseData.projectDetail.engagement_manager_locked;
		    }
		}
		let x = 0;
		if (
		    typeof state.projects != "undefined"
		) {
		    for (x = 0; x < Object.keys(state.projects).length; x++) {
		        if (typeof state.projects[x] != 'undefined' && state.projects[x].id == responseData.projectDetail.id) {
		            Vue.set(state.projects[x], "engagement_manager_locked", responseData.projectDetail.engagement_manager_locked);
		        }
		    }
		}
		if (
		    typeof state.userProjects != "undefined"
		) {
		    for (x = 0; x < Object.keys(state.userProjects).length; x++) {
		        if (typeof state.userProjects[x] != 'undefined' && state.userProjects[x].id == responseData.projectDetail.id) {
		            Vue.set(state.userProjects[x], "engagement_manager_locked", responseData.projectDetail.engagement_manager_locked);
		        }
		    }
		}

	},


    setProjectWithField: (state,responseData) => {
    	// state.projectDetail = responseData.projectDetail;
    	// console.log(state.projectDetail);
    	if (state.projectDetail.id) {
	    	if (state.projectDetail.id != responseData.projectDetail.id) {
    			state.projectDetail = responseData.projectDetail;
			  	// console.log(projectAndField.field);
		    	// console.log(responseData.projectDetail);
		    	// deepMergeJsonObjects(responseData.projectDetail, state.projectDetail);
		    } else {
		    	if (responseData.updatedProjectFieldValueResponse) {
		    		if (responseData.updatedProjectFieldValueResponse.ProjectPreviouslyUpdatedAt != state.projectDetail.updated_at) {
		    			// console.log(responseData.updatedProjectFieldValueResponse.ProjectPreviouslyUpdatedAt);
		    			// console.log(state.projectDetail.updated_at);
    					state.projectDetail = responseData.projectDetail;
		    		} else {
    					state.projectDetail.updated_at = responseData.projectDetail.updated_at;
		    		}
		    	}
		    }
		}
    	// loop through all deliverableTemplates and update the deliverableTemplate.
    	let x = 0;
    	if (
    	    typeof state.projects != "undefined"
    	) {
    	    for (x = 0; x < Object.keys(state.projects).length; x++) {
    	        if (typeof state.projects[x] != 'undefined' && state.projects[x].id == responseData.projectDetail.id) {
    	            Vue.set(state.projects, x, responseData.projectDetail);
    	        }
    	    }
    	}

    	if (
    	    typeof state.userProjects != "undefined"
    	) {
    	    for (x = 0; x < Object.keys(state.userProjects).length; x++) {
    	        if (typeof state.userProjects[x] != 'undefined' && state.userProjects[x].id == responseData.projectDetail.id) {
    	            Vue.set(state.userProjects, x, responseData.projectDetail);
    	        }
    	    }
    	}
    },

    setProject: (state, project) => {
        state.projectDetail = project;


        // loop through all deliverableTemplates and update the deliverableTemplate.
        let x = 0;
        if (
            typeof state.projects != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.projects).length; x++) {
                if (typeof state.projects[x] != 'undefined' && state.projects[x].id == project.id) {
                    Vue.set(state.projects, x, project);
                }
            }
        }
    },
    setProjectDeliverableTemplatesOnly: (state, deliverableTemplates) => {
        state.projectDeliverableTemplates = deliverableTemplates;
        state.projectDeliverableTemplates = sortTasks({sortKey: false, deliverableTemplates: state.projectDeliverableTemplates, return: true, sortFields: true})
    },
    setProjectDeliverableTemplates: (state, deliverableTemplates) => {
        state.projectDeliverableTemplates = deliverableTemplates.projectDeliverableTemplates.data;
        state.projectDeliverableTemplates = sortTasks({sortKey: false, deliverableTemplates: state.projectDeliverableTemplates, return: true, sortFields: true, namespace:state._namespace})
        state.allProjectDeliverableTemplatesPaginationData = deliverableTemplates.projectDeliverableTemplates;
    },
    setProjectDeliverable: (state, deliverableTemplate) => {
        if(state.projectDeliverableTemplates.length > 0) {
            state.projectDeliverableTemplates.forEach((value, key) => {
                if(value.id == deliverableTemplate.id) {
                    state.projectDeliverableTemplates[key].tasks = deliverableTemplate.tasks
                }
            })
        }
        // state.projectDeliverableTemplates = deliverableTemplates.projectDeliverableTemplates.data;
        // state.projectDeliverableTemplates = sortTasks({sortKey: false, deliverableTemplates: state.projectDeliverableTemplates, return: true, sortFields: true, namespace:state._namespace})
        // state.allProjectDeliverableTemplatesPaginationData = deliverableTemplates.projectDeliverableTemplates;
    },
    resetProjectDeliverableTemplates: (state, deliverableTemplates) => {
        state.projectDeliverableTemplates = [];
        state.allProjectDeliverableTemplatesPaginationData = {};
    },
    clearMergeDeliverableTemplates: (state, deliverableTemplates) => {
    	state.mergeDeliverableTemplates = [];
	},
	setProjectStatusForDeliverableTemplates: (state, payload) => {
		if(typeof state.projectDeliverableTemplates != 'undefined') {
		    for (let x = 0; x < Object.keys(state.projectDeliverableTemplates).length; x++) {
		        if (typeof state.projectDeliverableTemplates[x] != 'undefined') {
		        	if (state.projectDeliverableTemplates[x].project_id.id == payload.project_id) {
			        	state.projectDeliverableTemplates[x].project_id.project_status = payload.value;
			        }
		        }
		    }
		}

	},



    mergeDeliverableTemplates: (state, deliverableTemplates) => {
        if (typeof deliverableTemplates.allDeliverableTemplates != "undefined") {
            // delete deliverableTemplates.allDeliverableTemplates.data.totalOutlets
            state.mergeDeliverableTemplates = deliverableTemplates.allDeliverableTemplates.data;
            sortTasks({sortKey: false, deliverableTemplates: state.mergeDeliverableTemplates, sortFields: true, namespace:state._namespace})

        	for(let x = 0; x < state.mergeDeliverableTemplates.length; x++) {
        		let found = false;
            	for(let y = 0; y < state.deliverableTemplates.length; y++) {
            		if (state.deliverableTemplates[y].id == state.mergeDeliverableTemplates[x].id) {
            			found = true;
            		}
            	}
            	if (!found) {
					state.deliverableTemplates.push(state.mergeDeliverableTemplates[x]);
            		// resort it by publish date.
            		// state.deliverableTemplates = _.sortBy(state.deliverableTemplates,"is_master", "publish_date");
            	}
			}
			state.deliverableTemplates = _.chain(state.deliverableTemplates)
				.sortBy(function(deliverableTemplate) {
				   if (deliverableTemplate.is_master)
				       return Number.NEGATIVE_INFINITY;
				   else
				       return +deliverableTemplate.is_master;
				})
				.sortBy(function(deliverableTemplate) {
					if (deliverableTemplate.publish_date == null && deliverableTemplate.is_master == 0)
				           return Number.POSITIVE_INFINITY;
				       else
				           return Vue.moment(deliverableTemplate.publish_date);

				})
			  .value();
        }
    },





    setDeliverableTemplates: (state, deliverableTemplates) => {
        let now = Vue.moment().format("YYYY-MM-DD"),
            thisWeekStart = Vue.moment()
                .startOf("week")
                .format("YYYY-MM-DD"),
            thisWeekEnd = Vue.moment()
                .endOf("week")
                .format("YYYY-MM-DD"),
            nextWeekStart = Vue.moment()
                .add(1, "weeks")
                .startOf("week")
                .format("YYYY-MM-DD"),
            nextWeekEnd = Vue.moment()
                .add(1, "weeks")
                .endOf("week")
                .format("YYYY-MM-DD"),
            today = [],
            thisWeek = [],
            nextWeek = [],
            upcoming = [],
            noDeadline = [],
            overdue = [],
            projectOnHold = [],
            completed = [];

        if (typeof deliverableTemplates.allDeliverableTemplates != "undefined") {

            if (state.mergeDeliverableTemplates) {
            	for(let x = 0; x < state.mergeDeliverableTemplates.length; x++) {
            		let found = false;
	            	for(let y = 0; y < deliverableTemplates.allDeliverableTemplates.data.length; y++) {
	            		if (deliverableTemplates.allDeliverableTemplates.data[y].id == state.mergeDeliverableTemplates[x].id) {
	            			found = true;
	            		}
	            	}
	            	if (!found) {
	            		deliverableTemplates.allDeliverableTemplates.data.push(state.mergeDeliverableTemplates[x]);
	            	}
    			}
            }

            state.mergeDeliverableTemplates = [];

            state.deliverableTemplates = deliverableTemplates.allDeliverableTemplates.data;
            state.allDeliverableTemplatesPaginationData = deliverableTemplates.allDeliverableTemplates;

            sortTasks({sortKey: false, deliverableTemplates: state.deliverableTemplates, sortFields: true, namespace:state._namespace})
        }

        if (typeof deliverableTemplates.userDeliverableTemplates != "undefined") {
            if (typeof deliverableTemplates.userDeliverableTemplates.data != "undefined") {
                state.userDeliverableTemplatesPaginationData =
                    deliverableTemplates.userDeliverableTemplates;
                deliverableTemplates.userDeliverableTemplates.data.forEach(deliverableTemplate => {

                    let dueDate = false,
                        userId = "";
                    let taskDueDate = false;
            		let isThisWeek = false;
            		let isNextWeek = false;
            		let isToday = false;
            		let isOverdue = false;
            		let isNoDeadline = false;
            		let isComplete = false;
            		let isUpcoming = false;
            		let matchingUsers = false;
            		let taskMatchingUsers = false;
            		let isProjectOnHold = false;

                    if (
                        deliverableTemplate &&
                        deliverableTemplate.project_id &&
                        deliverableTemplate.project_id.project_status
                    ) {
                        if (
                            typeof deliverableTemplates.userDeliverableTemplates != "undefined" &&
                            deliverableTemplate.project_id.project_status.slug == "on-hold"
                        ) {
                            isProjectOnHold = true;
                        }
                    }

                    deliverableTemplate.tasks.forEach(task => {
                    	taskDueDate = false;
                    	taskMatchingUsers = false;
                    	userId = "";
                        task.taskFields.forEach((taskField, index) => {
                            if (taskField.field_id == 14) {
                                userId = taskField.value;
                                return;
                            }
                            if (taskField.field_id == 12) {
                                dueDate = taskField.value;
                                taskDueDate = taskField.value;
                            }

                            if (taskField.field_id == 4) {
                                if (typeof taskField.value == "object") {
                                    if (taskField.value.name == "Completed" || taskField.value.name == "Cancelled") {
                                        dueDate = false;
                    					taskDueDate = false;
                                    }
                                } else {
                                    if (JSON.parse(taskField.value).name == "Completed" || JSON.parse(taskField.value).name == "Cancelled") {
                                        dueDate = false;
                    					taskDueDate = false;
                                    }
                                }
                            }
                        });


                        if (userId == localStorage.getItem("userId")) {
                        	taskMatchingUsers = true;
                        	matchingUsers = true;
                        } else {
                        	// also match if they are filtering by user...
	                        if (state.myWeekDeliverableFilter) {
	                        	if (state.myWeekDeliverableFilter.coordinator) {
	                        		let x = 0;
	                        		for (x = 0; x < state.myWeekDeliverableFilter.coordinator.length; x++) {
	                        			if (state.myWeekDeliverableFilter.coordinator[x].id == userId) {
	                        				taskMatchingUsers = true;
	                        				matchingUsers = true;
	                        			}
	                        		}
	                        	}
	                        }
                        }

                        if (taskDueDate) {
                        	if (taskMatchingUsers) {
	                        	let compareDate = Vue.moment(taskDueDate).format(
	                        	    "YYYY-MM-DD"
	                        	);
	                        	if (now > compareDate) {
	                        	    if (deliverableTemplate.is_complete) {
	                        	        isComplete = true;
	                        	    } else {
	                        	        isOverdue = true;
	                        	    }
	                        	} else if (now == compareDate) {
	                        	    isToday = true;
	                        	} else if (
	                        	    compareDate >= thisWeekStart &&
	                        	    compareDate <= thisWeekEnd
	                        	) {
	                        	    isThisWeek = true;
	                        	} else if (
	                        	    compareDate >= nextWeekStart &&
	                        	    compareDate <= nextWeekEnd
	                        	) {
	                        	    isNextWeek = true;
	                        	} else {
	                        	    isUpcoming = true;
	                        	}
	                        }
                        } else {
                        	isNoDeadline = true;
                        }
                    });

                    if (isProjectOnHold) {
                        projectOnHold.push(deliverableTemplate);
                	} else if (isOverdue) {
                        overdue.push(deliverableTemplate);
                    } else if (isToday) {
                        today.push(deliverableTemplate);
                    } else if (isThisWeek) {
                        thisWeek.push(deliverableTemplate);
                    } else if (isNextWeek) {
                        nextWeek.push(deliverableTemplate);
                    } else if (isUpcoming) {
                        upcoming.push(deliverableTemplate);
                    } else if (isNoDeadline) {
                        noDeadline.push(deliverableTemplate);
                    } else if (isComplete) {
                        completed.push(deliverableTemplate);
                	}
                });
            } else {
            deliverableTemplates.userDeliverableTemplates.forEach(deliverableTemplate => {
                     let dueDate = false,
                         userId = "";
                     let taskDueDate = false;
             		let isThisWeek = false;
             		let isNextWeek = false;
             		let isToday = false;
             		let isOverdue = false;
             		let isNoDeadline = false;
             		let isComplete = false;
             		let isUpcoming = false;
             		let matchingUsers = false;
             		let taskMatchingUsers = false;

                     deliverableTemplate.tasks.forEach(task => {
                     	taskDueDate = false;
                     	taskMatchingUsers = false;
                     	userId = "";
                         task.taskFields.forEach((taskField, index) => {
                             if (taskField.field_id == 14) {
                                 userId = taskField.value;
                                 return;
                             }
                             if (taskField.field_id == 12) {
                                 dueDate = taskField.value;
                                 taskDueDate = taskField.value;
                             }
                             if (taskField.field_id == 4) {
                                 if (typeof taskField.value == "object") {
                                     if (taskField.value.name == "Completed" || taskField.value.name == "Cancelled") {
                                         dueDate = false;
                     					taskDueDate = false;
                                     }
                                 } else {
                                     if (JSON.parse(taskField.value).name == "Completed" || JSON.parse(taskField.value).name == "Cancelled") {
                                         dueDate = false;
                     					taskDueDate = false;
                                     }
                                 }
                             }
                         });


                         if (userId == localStorage.getItem("userId")) {
                         	taskMatchingUsers = true;
                         	matchingUsers = true;
                        } else {
                        	// also match if they are filtering by user...
	                        if (state.myWeekDeliverableFilter) {
	                        	if (state.myWeekDeliverableFilter.coordinator) {
	                        		let x = 0;
	                        		for (x = 0; x < state.myWeekDeliverableFilter.coordinator.length; x++) {
	                        			if (state.myWeekDeliverableFilter.coordinator[x].id == userId) {
	                        				taskMatchingUsers = true;
	                        				matchingUsers = true;
	                        			}
	                        		}
	                        	}
	                        }
                        }
                         if (taskDueDate) {
                         	if (taskMatchingUsers) {
 	                        	let compareDate = Vue.moment(taskDueDate).format(
 	                        	    "YYYY-MM-DD"
 	                        	);
 	                        	if (now > compareDate) {
 	                        	    if (deliverableTemplate.is_complete) {
 	                        	        isComplete = true;
 	                        	    } else {
 	                        	        isOverdue = true;
 	                        	    }
 	                        	} else if (now == compareDate) {
 	                        	    isToday = true;
 	                        	} else if (
 	                        	    compareDate >= thisWeekStart &&
 	                        	    compareDate <= thisWeekEnd
 	                        	) {
 	                        	    isThisWeek = true;
 	                        	} else if (
 	                        	    compareDate >= nextWeekStart &&
 	                        	    compareDate <= nextWeekEnd
 	                        	) {
 	                        	    isNextWeek = true;
 	                        	} else {
 	                        	    isUpcoming = true;
 	                        	}
 	                        }
                         } else {
                         	isNoDeadline = true;
                         }
                         });
                 	if (isOverdue) {
                         overdue.push(deliverableTemplate);
                     } else if (isToday) {
                         today.push(deliverableTemplate);
                     } else if (isThisWeek) {
                         thisWeek.push(deliverableTemplate);
                     } else if (isNextWeek) {
                         nextWeek.push(deliverableTemplate);
                     } else if (isUpcoming) {
                         upcoming.push(deliverableTemplate);
                     } else if (isNoDeadline) {
                         noDeadline.push(deliverableTemplate);
                     } else if (isComplete) {
                         completed.push(deliverableTemplate);
                 	}

                 });

            }
            state.unGroupedUserDeliverableTemplates = deliverableTemplates.userDeliverableTemplates;
            state.userDeliverableTemplates = {
                overdue,
                today,
                thisWeek,
                nextWeek,
                upcoming,
                noDeadline,
                projectOnHold,
                // completed
            };
            sortTasks({sortKey: false, deliverableTemplates: state.userDeliverableTemplates, sortFields: true, namespace:state._namespace})
        }
    },
    setCalendarDeliverableTemplates: (state, payload) => {
        state.calendarDeliverableTemplates = payload.allDeliverableTemplates.deliverableTemplates.data;
    },
    setYearCalendarDeliverableTemplates: (state, payload) => {
        state.yearCalendarDeliverableTemplates = payload.allDeliverableTemplates.deliverableTemplates.data;
    },
    setCalendarDeliverable: (state, deliverableTemplate) => {
        // loop through all deliverableTemplates and update the deliverableTemplate.
        let x = 0;
        // deliverableTemplate = sortTasks({sortKey: false, deliverableTemplates: [deliverableTemplate], return: true, sortFields: true, namespace:state._namespace})[0]
        if (
            typeof state.calendarDeliverableTemplates != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.calendarDeliverableTemplates).length; x++) {
                if (typeof state.calendarDeliverableTemplates[x] != 'undefined' && state.calendarDeliverableTemplates[x].id == deliverableTemplate.deliverableTemplateId) {
                    Vue.set(state.calendarDeliverableTemplates, x, deliverableTemplate);
                }
            }
        }
    },
    updateTaskFieldOrder: (state, payload) => {
    	if (typeof payload.namespace == "undefined") {
    		payload.namespace = state._namespace;
    	}
        sortTaskFields(payload)
    },
    updateTasksDisplayOrderValue: (state, payload) => {
        if(typeof state.deliverableTemplates != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverableTemplates).length; x++) {
                if (typeof state.deliverableTemplates[x] != 'undefined' && state.deliverableTemplates[x].id == payload.deliverableTemplateId) {
                    for(let y = 0; y < state.deliverableTemplates[x].tasks.length; y++) {
                        let tempTask = payload.taskData.filter((el) => {
                            return el.id == state.deliverableTemplates[x].tasks[y].id
                        })
                        state.deliverableTemplates[x].tasks[y].display_order = tempTask[0].display_order
                    }
                }
            }
        }

        if (typeof state.unGroupedUserDeliverableTemplates.data != "undefined" && state.unGroupedUserDeliverableTemplates.data.length) {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverableTemplates.data).length; x++) {
                if (state.unGroupedUserDeliverableTemplates.data[x].id == payload.deliverableTemplateId) {
                    for(let y = 0; y < state.unGroupedUserDeliverableTemplates.data[x].tasks.length; y++) {
                        let tempTask = payload.taskData.filter((el) => {
                            return el.id == state.unGroupedUserDeliverableTemplates.data[x].tasks[y].id
                        })
                        state.unGroupedUserDeliverableTemplates.data[x].tasks[y].display_order = tempTask[0].display_order
                    }
                }
            }
        }

        if (typeof state.deliverableTemplateDetail != "undefined") {
            if (state.deliverableTemplateDetail.id == payload.deliverableTemplateId) {
                for(let y = 0; y < state.deliverableTemplateDetail.tasks.length; y++) {
                    let tempTask = payload.taskData.filter((el) => {
                        return el.id == state.deliverableTemplateDetail.tasks[y].id
                    })
                    state.deliverableTemplateDetail.tasks[y].display_order = tempTask[0].display_order
                }
            }
        }
    },
    updateReorderedTasks: (state, payload) => {
        if(typeof state.deliverableTemplates != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverableTemplates).length; x++) {
                if (typeof state.deliverableTemplates[x] != 'undefined' && state.deliverableTemplates[x].id == payload.deliverableTemplateId) {
                    state.deliverableTemplates[x].tasks = payload.taskData
                }
            }
        }

        if(typeof state.projectDeliverableTemplates != 'undefined') {
            for (let x = 0; x < Object.keys(state.projectDeliverableTemplates).length; x++) {
                if (typeof state.projectDeliverableTemplates[x] != 'undefined' && state.projectDeliverableTemplates[x].id == payload.deliverableTemplateId) {
                    state.projectDeliverableTemplates[x].tasks = payload.taskData
                }
            }
        }

        if (typeof state.unGroupedUserDeliverableTemplates.data != "undefined" && state.unGroupedUserDeliverableTemplates.data.length) {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverableTemplates.data).length; x++) {
                if (state.unGroupedUserDeliverableTemplates.data[x].id == payload.deliverableTemplateId) {
                    state.unGroupedUserDeliverableTemplates.data[x].tasks = payload.taskData
                }
            }
        }

        if (typeof state.deliverableTemplateDetail != "undefined") {
            if (state.deliverableTemplateDetail.id == payload.deliverableTemplateId) {
                state.deliverableTemplateDetail.tasks = payload.taskData
            }
        }
    },
    updateFieldLabelText: (state, labelData) => {
        if(typeof state.deliverableTemplates != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverableTemplates).length; x++) {
                if (typeof state.deliverableTemplates[x] != 'undefined') {
                    for(let j = 0; j < Object.keys(state.deliverableTemplates[x].tasks).length; j++) {
                        if (typeof state.deliverableTemplates[x].tasks[j] != 'undefined') {
                            for(let k = 0; k < Object.keys(state.deliverableTemplates[x].tasks[j].taskFields).length; k++) {
                                if (typeof state.deliverableTemplates[x].tasks[j].taskFields[k] != 'undefined' && state.deliverableTemplates[x].tasks[j].taskFields[k].label_id === labelData.labelId) {
                                    state.deliverableTemplates[x].tasks[j].taskFields[k].label = labelData.labelText
                                }
                            }
                        }
                    }
                }
            }
        }

        if(typeof state.unGroupedUserDeliverableTemplates.data != 'undefined') {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverableTemplates.data).length; x++) {
                if (typeof state.unGroupedUserDeliverableTemplates.data[x] != 'undefined') {
                    for(let j = 0; j < Object.keys(state.unGroupedUserDeliverableTemplates.data[x].tasks).length; j++) {
                        if (typeof state.unGroupedUserDeliverableTemplates.data[x].tasks[j] != 'undefined') {
                            for(let k = 0; k < Object.keys(state.unGroupedUserDeliverableTemplates.data[x].tasks[j].taskFields).length; k++) {
                                if (typeof state.unGroupedUserDeliverableTemplates.data[x].tasks[j].taskFields[k] != 'undefined' && state.unGroupedUserDeliverableTemplates.data[x].tasks[j].taskFields[k].label_id === labelData.labelId) {
                                    state.unGroupedUserDeliverableTemplates.data[x].tasks[j].taskFields[k].label = labelData.labelText
                                }
                            }
                        }
                    }
                }
            }
        }
    },
    removeFieldFromAllTasks(state, labelId) {
        if(typeof state.deliverableTemplates != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverableTemplates).length; x++) {
                if (typeof state.deliverableTemplates[x] != 'undefined') {
                    for(let j = 0; j < Object.keys(state.deliverableTemplates[x].tasks).length; j++) {
                        if (typeof state.deliverableTemplates[x].tasks[j] != 'undefined') {
                            for(let k = 0; k < Object.keys(state.deliverableTemplates[x].tasks[j].taskFields).length; k++) {
                                if (typeof state.deliverableTemplates[x].tasks[j].taskFields[k] != 'undefined' && state.deliverableTemplates[x].tasks[j].taskFields[k].label_id === labelId) {
                                    state.deliverableTemplates[x].tasks[j].taskFields.splice(k, 1)
                                }
                            }
                        }
                    }
                }
            }
        }

        if(typeof state.unGroupedUserDeliverableTemplates.data != 'undefined') {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverableTemplates.data).length; x++) {
                if (typeof state.unGroupedUserDeliverableTemplates.data[x] != 'undefined') {
                    for(let j = 0; j < Object.keys(state.unGroupedUserDeliverableTemplates.data[x].tasks).length; j++) {
                        if (typeof state.unGroupedUserDeliverableTemplates.data[x].tasks[j] != 'undefined') {
                            for(let k = 0; k < Object.keys(state.unGroupedUserDeliverableTemplates.data[x].tasks[j].taskFields).length; k++) {
                                if (typeof state.unGroupedUserDeliverableTemplates.data[x].tasks[j].taskFields[k] != 'undefined' && state.unGroupedUserDeliverableTemplates.data[x].tasks[j].taskFields[k].label_id === labelId) {
                                    state.unGroupedUserDeliverableTemplates.data[x].tasks[j].taskFields.splice(k, 1)
                                }
                            }
                        }
                    }
                }
            }
        }
    },
    updateTaskData: (state, {deliverableTemplateData, deliverableTemplateId}) => {
        if(typeof state.deliverableTemplates != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverableTemplates).length; x++) {
                if (typeof state.deliverableTemplates[x] != 'undefined' && state.deliverableTemplates[x].id == deliverableTemplateId) {
                    state.deliverableTemplates[x].tasks = deliverableTemplateData.tasks
                }
            }
        }

        if (typeof state.unGroupedUserDeliverableTemplates.data != "undefined" && state.unGroupedUserDeliverableTemplates.data.length) {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverableTemplates.data).length; x++) {
                if (state.unGroupedUserDeliverableTemplates.data[x].id == deliverableTemplateId) {
                    state.unGroupedUserDeliverableTemplates.data[x].tasks = deliverableTemplateData.tasks
                }
            }
        }

        if (typeof state.deliverableTemplateDetail != "undefined") {
            if (state.deliverableTemplateDetail.id == deliverableTemplateId) {
                state.deliverableTemplateDetail.tasks = deliverableTemplateData.tasks
            }
        }
    },
    setGroupedUserDeliverableTemplatesOnly: (state, deliverableTemplate) => {
        state.userDeliverableTemplates = deliverableTemplate;
    },
    setDeliverableTemplatesOnly: (state, deliverableTemplate) => {
        state.deliverableTemplates = deliverableTemplate;
    },
       setDeliverablePublishDate: (state, payload) => {
        //{id:payload.id, publish_date:value});
        // loop through all deliverableTemplates and update the deliverableTemplate publish date based on what was sent.
        let x = 0;
        let xx = 0;
        if (
            typeof state.deliverableTemplates != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.deliverableTemplates).length; x++) {
                if (typeof state.deliverableTemplates[x] != 'undefined') {

                    if (state.deliverableTemplates[x].id == payload.id) {
                        Vue.set(state.deliverableTemplates[x], "publish_date", payload.value);
                    }
                    if (typeof state.deliverableTemplateDetail.social_series_deliverableTemplates != "undefined") {
                         for (xx = 0; xx < Object.keys(state.deliverableTemplateDetail.social_series_deliverableTemplates).length; xx++) {
                            if (state.deliverableTemplateDetail.social_series_deliverableTemplates[xx].id == payload.id) {
                                Vue.set(state.deliverableTemplateDetail.social_series_deliverableTemplates[xx], "publish_date", payload.value);
                            }
                         }
                    }
                }


            }
        }
    },
    setDeliverable: (state, deliverableTemplate) => {
        // loop through all deliverableTemplates and update the deliverableTemplate.
        if (!deliverableTemplate) return;
        
        let x = 0;
        const sorted = sortTasks({sortKey: false, deliverableTemplates: [deliverableTemplate], return: true, sortFields: true, namespace:state._namespace});
        if (!sorted || !sorted[0]) {
            deliverableTemplate = deliverableTemplate;
        } else {
            deliverableTemplate = sorted[0];
        }
        if (
            typeof state.deliverableTemplates != "undefined"
	        ) {
	            for (x = 0; x < Object.keys(state.deliverableTemplates).length; x++) {
	                if (typeof state.deliverableTemplates[x] != 'undefined' && state.deliverableTemplates[x].id == deliverableTemplate.id) {
	                    Vue.set(state.deliverableTemplates, x, deliverableTemplate);
	                }
	            }
	        }

    if (
        typeof state.allDeliverableTemplates != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.allDeliverableTemplates).length; x++) {
                if (typeof state.allDeliverableTemplates[x] != 'undefined' && state.allDeliverableTemplates[x].id == deliverableTemplate.id) {
                    Vue.set(state.allDeliverableTemplates, x, deliverableTemplate);
                }
            }
        }
    },
    groupUserDeliverable: state => {
        if (
            typeof state.unGroupedUserDeliverableTemplates.data == "undefined" ||
            !state.unGroupedUserDeliverableTemplates.data.length
        ) {
            return;
        }
        let now = Vue.moment().format("YYYY-MM-DD"),
            thisWeekStart = Vue.moment()
                .startOf("week")
                .format("YYYY-MM-DD"),
            thisWeekEnd = Vue.moment()
                .endOf("week")
                .format("YYYY-MM-DD"),
            nextWeekStart = Vue.moment()
                .add(1, "weeks")
                .startOf("week")
                .format("YYYY-MM-DD"),
            nextWeekEnd = Vue.moment()
                .add(1, "weeks")
                .endOf("week")
                .format("YYYY-MM-DD"),
            today = [],
            thisWeek = [],
            nextWeek = [],
            upcoming = [],
            noDeadline = [],
            overdue = [],
            completed = [];

        state.unGroupedUserDeliverableTemplates.data.forEach(deliverableTemplate => {
        	let dueDate = false,
        	    userId = "";
        	let taskDueDate = false;
        	let isThisWeek = false;
        	let isNextWeek = false;
        	let isToday = false;
        	let isOverdue = false;
        	let isNoDeadline = false;
        	let isComplete = false;
        	let isUpcoming = false;
            let matchingUsers = false;
        	let taskMatchingUsers = false;

        	deliverableTemplate.tasks.forEach(task => {
        		taskDueDate = false;
        		taskMatchingUsers = false;
        		userId = "";
        	    task.taskFields.forEach((taskField, index) => {
        	        if (taskField.field_id == 14) {
        	            userId = taskField.value;
        	            return;
        	        }
        	        if (taskField.field_id == 12) {
        	            dueDate = taskField.value;
        	            taskDueDate = taskField.value;
        	        }
        	        if (taskField.field_id == 4) {
        	            if (typeof taskField.value == "object") {
        	                if (taskField.value.name == "Completed" || taskField.value.name == "Cancelled") {
        	                    dueDate = false;
        						taskDueDate = false;
        	                }
        	            } else {
        	                if (JSON.parse(taskField.value).name == "Completed" || JSON.parse(taskField.value).name == "Cancelled") {
        	                    dueDate = false;
        						taskDueDate = false;
        	                }
        	            }
        	        }
        	    });


        	    if (userId == localStorage.getItem("userId")) {
        	    	taskMatchingUsers = true;
        	    	matchingUsers = true;
	            } else {
	            	// also match if they are filtering by user...
	                if (state.myWeekDeliverableFilter) {
	                	if (state.myWeekDeliverableFilter.coordinator) {
	                		let x = 0;
	                		for (x = 0; x < state.myWeekDeliverableFilter.coordinator.length; x++) {
	                			if (state.myWeekDeliverableFilter.coordinator[x].id == userId) {
	                				taskMatchingUsers = true;
	                				matchingUsers = true;
	                			}
	                		}
	                	}
	                }
	            }


        	    if (taskDueDate) {
        	    	if (taskMatchingUsers) {
        	        	let compareDate = Vue.moment(taskDueDate).format(
        	        	    "YYYY-MM-DD"
        	        	);
        	        	if (now > compareDate) {
        	        	    if (deliverableTemplate.is_complete) {
        	        	        isComplete = true;
        	        	    } else {
        	        	        isOverdue = true;
        	        	    }
        	        	} else if (now == compareDate) {
        	        	    isToday = true;
        	        	} else if (
        	        	    compareDate >= thisWeekStart &&
        	        	    compareDate <= thisWeekEnd
        	        	) {
        	        	    isThisWeek = true;
        	        	} else if (
        	        	    compareDate >= nextWeekStart &&
        	        	    compareDate <= nextWeekEnd
        	        	) {
        	        	    isNextWeek = true;
        	        	} else {
        	        	    isUpcoming = true;
        	        	}
        	        }
        	    } else {
        	    	isNoDeadline = true;
        	    }
        	    });

        	if (isOverdue) {
        	    overdue.push(deliverableTemplate);
        	} else if (isToday) {
        	    today.push(deliverableTemplate);
        	} else if (isThisWeek) {
        	    thisWeek.push(deliverableTemplate);
        	} else if (isNextWeek) {
        	    nextWeek.push(deliverableTemplate);
        	} else if (isUpcoming) {
        	    upcoming.push(deliverableTemplate);
        	} else if (isNoDeadline) {
        	    noDeadline.push(deliverableTemplate);
        	} else if (isComplete) {
        	    completed.push(deliverableTemplate);
        	}

        });
        state.userDeliverableTemplates = {
            overdue,
            today,
            thisWeek,
            nextWeek,
            upcoming,
            noDeadline
            // completed
        };
    },
    setDeliverableContacts: (state, data) => {
        let x = 0;
        for (x = 0; x < state.deliverableTemplates.length; x++) {
            if (state.deliverableTemplates[x].id == data.deliverableTemplateId) {
                Vue.set(state.deliverableTemplates[x].contacts, data.contacts);
            }
        }
        if (
            typeof state.unGroupedUserDeliverableTemplates.data != "undefined" &&
            state.unGroupedUserDeliverableTemplates.data.length
        ) {
            for (x = 0; x < state.unGroupedUserDeliverableTemplates.length; x++) {
                if (
                    state.unGroupedUserDeliverableTemplates[x].id == data.deliverableTemplateId
                ) {
                    Vue.set(
                        state.unGroupedUserDeliverableTemplates[x].contacts,
                        data.contacts
                    );
                }
            }
        }
    },

	setComponentState:(state, component) => {
    	let x = 0;
    	let found = false;

    	if (typeof state.componentState.length == "undefined" || state.componentState == null) {
    		state.componentState = array();
    	}
    	for (x = 0; x < state.componentState.length; x++) {
    	    if (state.componentState[x].slug == component.slug) {
    	    	found = true;
    	    }
    	}
    	if (!found) {
    		if (typeof component.slug != "undefined") {
    			state.componentState.push(component);
    			return;
    		} else {
    			// bad component.
    			return;
    		}
    	}
    	for (x = 0; x < state.componentState.length; x++) {
    	    if (state.componentState[x].slug == component.slug) {
	          Vue.set(state.componentState, x, component);
    	    }
    	}
    	localStorage.setItem('componentState', JSON.stringify(state.componentState));
	},


    setActiveTabIndex:(state, tab) => {
    	let x = 0;
    	let found = false;

    	if (typeof state.activeTab.length == "undefined" || state.activeTab == null) {
    		state.activeTab = array();
    	}
    	for (x = 0; x < state.activeTab.length; x++) {
    	    if (state.activeTab[x].slug == tab.slug) {
    	    	found = true;
    	    }
    	}
    	if (!found) {
    		state.activeTab.push({slug:tab.slug, index:tab.index});
    		return;
    	}
    	for (x = 0; x < state.activeTab.length; x++) {
    	    if (state.activeTab[x].slug == tab.slug) {
	          Vue.set(state.activeTab, x, tab);
    	    }
    	}
    	localStorage.setItem('activeTab', JSON.stringify(state.activeTab));

    },

    setTask: (state, task) => {
        let x = 0;
        let xx = 0;
        for (x = 0; x < state.deliverableTemplates.length; x++) {
            if (state.deliverableTemplates[x].id == task.deliverableTemplateId) {
                for (xx = 0; xx < state.deliverableTemplates[x].tasks.length; xx++) {
                    if (state.deliverableTemplates[x].tasks[xx].id == task.task.id) {
                        Vue.set(state.deliverableTemplates[x].tasks, xx, task.task);
                    }
                }
            }
        }
    },

    setCustomFields: (state, customFields) =>
        (state.customFields = customFields),
    setContacts: (state, contacts) => (state.contacts = contacts),
    setActiveContacts: (state, contacts) => (state.activeContacts = contacts),
    setProjectDetail: (state, projectDetail) => {
        state.projectDetail = typeof projectDetail.data == "undefined" ? projectDetail : projectDetail.data
    },
    resetProjectDetail: (state) => {
        state.projectDetail = []
    },

    setDeliverableTemplateDetailPublishDetails: (state, deliverableTemplateDetail) => {
			// updating a normal deliverableTemplate (not master series deliverableTemplate)
			if (state.deliverableTemplateDetail.id == deliverableTemplateDetail.id) {
				// set the publish status, etc.
				state.deliverableTemplateDetail.publish_date = deliverableTemplateDetail.publish_date;
				state.deliverableTemplateDetail.publish_status = deliverableTemplateDetail.publish_status;
				state.deliverableTemplateDetail.publish_status_id = deliverableTemplateDetail.publish_status_id;
				state.deliverableTemplateDetail.manually_updated_at = deliverableTemplateDetail.manually_updated_at;
				if (typeof deliverableTemplateDetail.actions != "undefined") {
					state.deliverableTemplateDetail.actions = deliverableTemplateDetail.actions;
				}

			}

			if (typeof state.deliverableTemplateDetail.social_series_deliverableTemplates != "undefined") {
				// loop through the social series deliverableTemplates to see if this one is in there.
				for(let x = 0; x < state.deliverableTemplateDetail.social_series_deliverableTemplates.length; x++) {
					if (state.deliverableTemplateDetail.social_series_deliverableTemplates[x].id == deliverableTemplateDetail.id) {
						// set the publish status, etc.
						state.deliverableTemplateDetail.social_series_deliverableTemplates[x].publish_date = deliverableTemplateDetail.publish_date;
						state.deliverableTemplateDetail.social_series_deliverableTemplates[x].publish_status = deliverableTemplateDetail.publish_status;
						state.deliverableTemplateDetail.social_series_deliverableTemplates[x].publish_status_id = deliverableTemplateDetail.publish_status_id;
						state.deliverableTemplateDetail.social_series_deliverableTemplates[x].manually_updated_at = deliverableTemplateDetail.manually_updated_at;

						if (typeof deliverableTemplateDetail.actions != "undefined") {
							state.deliverableTemplateDetail.social_series_deliverableTemplates[x].actions = deliverableTemplateDetail.actions;
						}

					}
				}
			}
	},

	setShowPostContentInListing:(state, data) => {
		state.showPostContentLangInListing = data.lang_id;
		state.showPostContentNetworkInListing = data.social_network_type_id;
	},

    setDeliverableTemplateDetailSocialPostNetworkShareUrl: (state, data) => {
    	/*
    	{
    		payload:REQUEST data sent to server
    		resopnse: response from
    	}
    	*/
		if (state.deliverableTemplates) {
			for(let z = 0; z < state.deliverableTemplates.length; z++) {
				if (state.deliverableTemplates[z].id == state.deliverableTemplateDetail.id) {
					if (state.deliverableTemplates[z].social_posts) {
						for(let zz = 0; zz < state.deliverableTemplates[z].social_posts.length; zz++) {
							if (state.deliverableTemplates[z].social_posts[zz].id == data.payload.id) {

								// now find the share url.
								for(let zzz = 0; zzz < state.deliverableTemplates[z].social_posts[zz].share_urls.length; zzz++) {
										if (state.deliverableTemplates[z].social_posts[zz].share_urls[zzz].id == data.payload.social_network_id) {
		        							Vue.set(state.deliverableTemplates[z].social_posts[zz].share_urls[zzz], url, data.payload.value);
		        							if (data.payload.only_share_url) {
		        								Vue.set(state.deliverableTemplates[z].social_posts[zz].share_urls[zzz], only_share_url, 1);
		        							} else  {
		        								Vue.set(state.deliverableTemplates[z].social_posts[zz].share_urls[zzz], only_share_url, 0);

		        							}
										}

								}
							}
						}
					}
		        }
			}
		}

    },

        setDeliverableTemplateDetailOnly: (state, deliverableTemplateDetail) => {
            (state.deliverableTemplateDetail =
                typeof deliverableTemplateDetail.data == "undefined"
                    ? deliverableTemplateDetail
                    : deliverableTemplateDetail.data)


            sortTasks({sortKey: false, deliverableTemplates: state.deliverableTemplateDetail, sortFields: true, namespace:state._namespace})
        },


    replaceDeliverableTemplateDetail:(state, payload)=> {
		if (state.deliverableTemplates) {
			for(let z = 0; z < state.deliverableTemplates.length; z++) {
				if (state.deliverableTemplates[z].id == payload.id) {
		        	Vue.set(state.deliverableTemplates, z, _.cloneDeep(payload.deliverableTemplateDetail));
		        }
			}
		}
    },

    setDeliverableTemplateDetail: (state, deliverableTemplateDetail) => {
        (state.deliverableTemplateDetail =
            typeof deliverableTemplateDetail.data == "undefined"
                ? deliverableTemplateDetail
                : deliverableTemplateDetail.data)
      sortTasks({sortKey: false, deliverableTemplates: state.deliverableTemplateDetail, sortFields: true, namespace:state._namespace})
    },

    setSocialSeriesDetail: (state, socialSeriesDetail) => (state.socialSeriesDetail = socialSeriesDetail),
    setDepartments: (state, departments) => (state.departments = departments),
    setAllSocialSeries: (state, socialSeries) => {
    	state.socialSeries = socialSeries;
     },
    setAllSocialCampaigns: (state, socialCampaigns) => (state.socialCampaigns = socialCampaigns),
    setListSocialSeries: (state, socialSeries) => (state.listSocialSeries = socialSeries),
    setListSocialCampaigns: (state, socialCampaigns) => {
	    state.listSocialCampaigns = socialCampaigns;
    },
    setSocialSeries: (state, socialSeries) => {
        let x = 0;
        let y = 0;
        if (
            typeof state.socialSeries != "undefined"
        ) {
            for (y = 0; y < Object.keys(socialSeries).length; y++) {
	            for (x = 0; x < Object.keys(state.socialSeries).length; x++) {
	                if (typeof state.socialSeries[x] != 'undefined' && state.socialSeries[x].id == socialSeries[y].id) {
	                    Vue.set(state.socialSeries, x, socialSeries[y]);
	                }
	            }
	        }
        }
        if (
            typeof state.listSocialSeries != "undefined"
        ) {
            for (y = 0; y < Object.keys(socialSeries).length; y++) {
	            for (x = 0; x < Object.keys(state.listSocialSeries).length; x++) {
	                if (typeof state.listSocialSeries[x] != 'undefined' && state.listSocialSeries[x].id == socialSeries[y].id) {
	                    Vue.set(state.listSocialSeries, x, socialSeries[y]);
	                }
	            }
	        }
        }
    },


    setOpenWithFocus: (state, control) => {
    	state.openWithFocus = control;
    },

    setSocialCampaign: (state, socialCampaign) => {

    	let resortCampaigns = false;
        // loop through all deliverableTemplates and update the deliverableTemplate.
        let x = 0;
        // deliverableTemplate = sortTasks({sortKey: false, deliverableTemplates: [deliverableTemplate], return: true, sortFields: true}, namespace:state._namespace)[0]
        if (
            typeof state.socialCampaigns != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.socialCampaigns).length; x++) {
                if (typeof state.socialCampaigns[x] != 'undefined' && state.socialCampaigns[x].id == socialCampaign.id) {
                    Vue.set(state.socialCampaigns, x, socialCampaign);
                }
            }
        }

        if (
            typeof state.listSocialCampaigns != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.listSocialCampaigns).length; x++) {
                if (typeof state.listSocialCampaigns[x] != 'undefined' && state.listSocialCampaigns[x].id == socialCampaign.id) {
                	if (state.listSocialCampaigns[x].title != socialCampaign.title) {
                		resortCampaigns = true;
                	}

                    Vue.set(state.listSocialCampaigns, x, socialCampaign);

                }
            }

			if (resortCampaigns) {
				state.listSocialCampaigns = _.sortBy(state.listSocialCampaigns, "title");
			}
        }

    },
    setActiveDepartments: (state, departments) => (state.activeDepartments = departments),
    setTags: (state, tags) => {
        state.tags = tags.allTags;
        state.trendingTags = tags.trendingTags;
    },
    setAllRoles: (state, roles) => {
        state.allRoles = roles;
    },
    setDeliverableTemplateRoles: (state, roles) => {
        state.deliverableTemplateRoles = roles
    },
    setAllAbilities: (state, abilities) => {
        state.allAbilities = abilities;
    },
    setSubjects: (state, subjects) => (state.subjects = subjects),
    toggleEditDeliverableModal: (state, type) =>
        (state.editDeliverableModal =
            typeof type != "undefined" && type == "hide"
                ? false
                : !state.editDeliverableModal),
    toggleEditProjectModal: (state, type) =>
        (state.editProjectModal =
            typeof type != "undefined" && type == "hide"
                ? false
                : !state.editProjectModal),
    toggleAddContactModal: (state, type) =>
        (state.addContactModal =
            typeof type != "undefined" && type == "hide"
                ? false
                : !state.addContactModal),
    toggleEditContactModal: (state, type) =>
        (state.editContactModal =
            typeof type != "undefined" && type == "hide"
                ? false
                : !state.editContactModal),
    toggleEditSocialModal: (state, type) =>
                (state.editSocialModal =
                    typeof type != "undefined" && type == "hide"
                        ? false
                        : !state.editSocialModal),
    setUsers: (state, users) => (state.users = users),
    setActiveUsers: (state, users) => (state.activeUsers = users),
    setGeoData: (state, geoData) => {
        state.countries = geoData.countries;
        state.regions = geoData.regions;
        state.countryStates = geoData.states;
    },
    removeMediaCallFromList: (state, deliverableTemplateId) => {
        if(typeof state.allDeliverableTemplatesPaginationData.data != 'undefined') {
            let deliverableTemplateArr = Object.values(state.allDeliverableTemplatesPaginationData.data);
            deliverableTemplateArr.splice(-1, 1);
            let filteredArr = deliverableTemplateArr.filter(function(value, index, arr){
                return value.id != deliverableTemplateId;
            });
            state.allDeliverableTemplatesPaginationData.data = {...filteredArr};
            state.allDeliverableTemplatesPaginationData.total -= 1;
            state.deliverableTemplates = state.allDeliverableTemplatesPaginationData.data;
        }
    },
    setDataLoading: (state, loading) => {
        state.dataLoading = Boolean(loading);
    },
    setDeliverableTemplateCount: (state, count) => {
        state.deliverableTemplateCount = count;
    },
    setTasksToHighlight: (state, taskIdsArray) => {
        state.tasksToHighlight = taskIdsArray;
    },
    setTaskHighlighted: (state, taskId) => {
        state.taskHighlighted = taskId;
    },
    setNewProjectCreated: (state, newProjectCreated) => {
        state.newProjectCreated = newProjectCreated;
    },
    setProjectSort: (state, value) => {
        state.projectSort = value;
    },
    setMyProjectSort: (state, value) => {
        state.myProjectSort = value;
    },
    setSystemSettings:(state, systemSettings) => {
        state.systemSettings = systemSettings;
    },
    setSocialCampaignDetail: (state, socialCampaignDetail) => {
        state.socialCampaignDetail = socialCampaignDetail;
    },
    setSocialGroupBy: (state, socialGroupBy) => {
        state.socialGroupBy = socialGroupBy;
    }
};

const getters = {
    newDeliverableTemplates: state => state.newDeliverableTemplates,
	allDeliverableTemplates: state => state.allDeliverableTemplates,
	showPostContentLangInListing: state => state.showPostContentLangInListing,
	showPostContentNetworkInListing: state => state.showPostContentNetworkInListing,
	openWithFocus: state => state.openWithFocus,
	refreshKey: state => state.refreshKey,
    currentContactData: state => state.currentContactData,
	currentFieldKey: state => state.currentFieldKey,
	deliverableTemplateId: state => state.deliverableTemplateId,
	newContactName: state => state.newContactName,
	currentContactTemplate: state => state.currentContactTemplate,
	calendarFilter: state => state.calendarFilter,
    calendarEvents: state => state.calendarEvents,
	projectCalendarFilter: state => state.projectCalendarFilter,
    projectCalendarEvents: state => state.projectCalendarEvents,
    preferenceData: state => state.preferenceData,
    loggedInUserData: state => state.loggedInUserData,
    deliverableTemplates: state => state.deliverableTemplates,
    preferenceDeliverableTypes: state => state.preferenceDeliverableTypes,
    auditLogFilter: state => state.auditLogFilter,
    auditLogs: state => state.auditLogs,
    auditLogTags: state => state.auditLogTags,
    auditLogContacts: state => state.auditLogContacts,
    auditLogDepartments: state => state.auditLogDepartments,
    auditLogCoordinators: state => state.auditLogCoordinators,
    auditLogDeliverableTemplates: state => state.auditLogDeliverableTemplates,
    auditLogProjects: state => state.auditLogProjects,
    activateUndo: state => state.activateUndo,
    activateUndoText: state => state.activateUndoText,
    activateUndoNoScroll: state => state.activateUndoNoScroll,
    activateMyProjectUndo: state => state.activateMyProjectUndo,
    activateMyProjectUndoId: state => state.activateMyProjectUndoId,
    breadcrumbs: state => state.breadcrumbs,
    notification: state => state.notification,
    allAlerts: state => state.allAlerts,
    userAlerts: state => state.userAlerts,
    userViews: state => state.userViews,
    GSRecentSearch: state => {
        let uniqueSearches = [];
        uniqueSearches['all'] = [...new Set(state.GSRecentSearch['all'])];
        uniqueSearches['tags'] = [...new Set(state.GSRecentSearch['tags'])];
        uniqueSearches['contacts'] = [...new Set(state.GSRecentSearch['contacts'])];
        uniqueSearches['coordinators'] = [...new Set(state.GSRecentSearch['coordinators'])];
        uniqueSearches['departments'] = [...new Set(state.GSRecentSearch['departments'])];
        return uniqueSearches;
    },
    currentPage: state => state.currentPage,
    allRoles: state => state.allRoles,
    deliverableTemplateRoles: state => state.deliverableTemplateRoles,
    allAbilities: state => state.allAbilities,
    initialContacts: state => state.initialContacts,
    initialTags: state => state.initialTags,
    initialDepartments: state => state.initialDepartments,
    outletCount: state => state.outletCount,
    defaultFilterParams: state => state.defaultFilterParams,
    deliverableTemplateFilter: state => state.deliverableTemplateFilter,
    mediaCallFilter: state => state.mediaCallFilter,
    myWeekDeliverableFilter: state => state.myWeekDeliverableFilter,
    calendarFilter: state => state.calendarFilter,
    addProjectDeliverableFilter: state => state.addProjectDeliverableFilter,
    projectDeliverableListFilter: state => state.projectDeliverableListFilter,
    globalSearchFilter: state => state.globalSearchFilter,
    projectFilter: state => state.projectFilter,
    myProjectFilter: state => state.myProjectFilter,
    reportFilter: state => state.reportFilter,
	socialMediaFilter: state => state.socialMediaFilter,
	socialMediaCalendarFilter: state => state.socialMediaCalendarFilter,
    globalSearchData: state => state.globalSearchData,
    projects: state => state.projects,
    allProjects: state => state.allProjects,
    systemSettings: state => state.systemSettings,
    userProjects: state => state.userProjects,
    userManageProjects: state => state.userManageProjects,
    userManageSocialCampaigns: state => state.userManageSocialCampaigns,
    userHasProjects: state => state.userHasProjects,
    mergeDeliverableTemplates: state=> state.mergeDeliverableTemplates,
    listSocialSeries: state => state.listSocialSeries,
    listSocialCampaigns: state => state.listSocialCampaigns,
    socialSeries: state => state.socialSeries,
    socialCampaigns: state => state.socialCampaigns,
    allCalendarDeliverableTemplates: state => state.calendarDeliverableTemplates,
    allYearCalendarDeliverableTemplates: state => state.yearCalendarDeliverableTemplates,
	getSocialSeriesWithSocialCampaign:(state) => (social_campaign_id) => {
	  if (state.socialSeries) {
	    return state.socialSeries.filter((entry) => {
	    	if (entry.social_campaign_id == social_campaign_id) {
	    			return true;
	    	}
	      return false;
	    })
	  }
	},

	getComponentState:(state) => (slug, default_obj) => {
		if (typeof state.componentState.length == "undefined" || state.componentState == null) {
			state.componentState = array();
		}
		  if (state.componentState) {
		    var item =  state.componentState.filter((entry) => {
		    	if (entry.slug == slug) {
		    		return true;
		    	}
		    })
		    if (item.length > 0) {
		    	return item[0];
		    }
		  }
		  if (typeof default_obj != "undefined") {
		  	return default_obj;
		  }
		  return {};
	},


	getActiveTabIndex:(state) => (tab_slug) => {
		if (typeof state.activeTab.length == "undefined" || state.activeTab == null) {
			state.activeTab = array();
		}
	  if (state.activeTab) {
	    var item =  state.activeTab.filter((entry) => {
	    	if (entry.slug == tab_slug) {
	    		return true;
	    	}
	    })
	    if (item.length > 0) {
	    	return item[0].index;
	    }
	  }
	  // return the first tab index.
	  return 0;
	},

	getSocialSeriesWithoutSocialCampaign:(state) => () => {
	  if (state.socialSeries) {
	    return state.socialSeries.filter((entry) => {
	    	if (entry.social_campaign_id == null) {
	    			return true;
	    	}
	      return false;
	    })
	  }
	},

	getDeliverableTemplateSlug:(state) => (template_id) => {
	  if (state.allDeliverableTypes) {
	    var item =  state.allDeliverableTypes.filter((entry) => {
	    	if (entry.typeId == template_id) {
	    		return true;
	    	}
	      return false;
	    })
	    if (item) {
	    	if (item.length > 0) {
	    		return item[0].slug;
	    	}
	    }
	    return false;
	  }
	  return false;
	},

	getSystemSetting:(state) => (keyToCheck) => {
		if (state.systemSettings.settings) {
	        const theSetting = state.systemSettings.settings.find(setting => setting.setting_key === keyToCheck);
	        return theSetting?.setting_value?.value || false;
    	} else {
    		return false;
    	}
    },

	getListSocialSeriesWithSocialCampaign:(state) => (social_campaign_id) => {
	  if (state.listSocialSeries) {
	    return state.listSocialSeries.filter((entry) => {
	    	if (entry.social_campaign_id == social_campaign_id) {
	    			return true;
	    	}
	      return false;
	    })
	  }
	},

	getListSocialSeriesWithoutSocialCampaign:(state) => () => {
	  if (state.listSocialSeries) {
	    return state.listSocialSeries.filter((entry) => {
	    	if (entry.social_campaign_id == null) {
	    			return true;
	    	}
	      return false;
	    })
	  }
	},



    getSocialMediaOnlyCalendar (state) {
	  if (state.deliverableTemplates) {
	    return state.deliverableTemplates.filter((deliverableTemplate) => {
	    	if (typeof deliverableTemplate.social_campaigns != "undefined") {
	    		if (typeof deliverableTemplate.social_series != "undefined") {
			    	if (deliverableTemplate.social_series.length == 0 && deliverableTemplate.social_campaigns.length == 0) 			{
			    		return true;
			    	}
			    } else {
			    	return true;
			    }
		    } else {
		    	return true;
		    }
	      return false;
	    })
	  }
	},

    getLangNameById:(state) => (id) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.languages) {
    			for (let i in state.systemSettings.languages) {
    				if (state.systemSettings.languages[i].id == id) {
    					return state.systemSettings.languages[i].lang_name;
    				}
    			}
    		}
    	}
    	return null;
	},

    getPublishStatusTextBySlug:(state) => (slug) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.publish_status) {
    			for (let i in state.systemSettings.publish_status) {
    				if (state.systemSettings.publish_status[i].slug == slug) {
    					return state.systemSettings.publish_status[i].name;
    				}
    			}
    		}
    	}
    	return null;
	},



    getLangCodeById:(state) => (id) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.languages) {
    			for (let i in state.systemSettings.languages) {
    				if (state.systemSettings.languages[i].id == id) {
    					return state.systemSettings.languages[i].lang_code;
    				}
    			}
    		}
    	}
    	return null;
	},
    getSocialNetworkTypeById:(state) => (id) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.social_networks) {
    			for (let i in state.systemSettings.social_networks) {
    				if (state.systemSettings.social_networks[i].id == id) {
    					return state.systemSettings.social_networks[i].api_type;
    				}
    			}
    		}
    	}
    	return null;
	},

	getAvailableSocialNetworks:(state) => {
	    let socialNetworkOptions = [];
	      if(state.systemSettings.social_networks && state.systemSettings.social_network_clients){
	        state.systemSettings.social_networks.forEach(function(network){
	        	state.systemSettings.social_network_clients.forEach(function(client){
	        		if (network.id == client.social_network_id && client.is_enabled == 1) {
			        // only show the entry if there are accounts for the
			          socialNetworkOptions.push(network);
		        	}
	        	});
	        })
	      }
	      return socialNetworkOptions;
	 },

    getSocialNetworkClassById:(state, getters) => (id) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.social_networks) {
    			for (let i in state.systemSettings.social_networks) {
    				if (state.systemSettings.social_networks[i].id == id) {
    					return getters.getSocialNetworkClass(state.systemSettings.social_networks[i].api_type);
    				}
    			}
    		}
    	}
    	return null;
	},
	getSocialNetworkClass: (state) => (api_type) => {
		if (api_type == "twitter") {
			return "x-twitter";
		}
		return api_type;
      },


    getSocialCampaignNameById:(state) => (campaign_id) => {
    	if (state.socialCampaigns) {
    		var out = state.socialCampaigns.filter((item) => {
    			if (item.id == campaign_id) {
    				return true;
    			}
    			return false;
    		})
    		if (out) {
    			if (out.length > 0) {
    				return out[0].title;
    			}
    		}
    	}
    	return null;
	},


    getSocialSeriesNameById:(state) => (series_id) => {
    	if (state.socialSeries) {
    		var out = state.socialSeries.filter((item) => {
    			if (item.id == series_id) {
    				return true;
    			}
    			return false;
    		})
    		if (out) {
    			if (out.length > 0) {
    				return out[0].title;
    			}
    		}
    	}
    	return null;
	},





    getSocialMediaWithSocialCampaignAndSeries:(state) => (social_campaign_id, social_series_id) => {
	  if (state.deliverableTemplates) {
	    return state.deliverableTemplates.filter((deliverableTemplate) => {

	    	if (deliverableTemplate.social_campaigns.length > 0) {
	    		var foo = deliverableTemplate.social_campaigns.filter((id) => {
	    			if (id == social_campaign_id) {
	    				return true;
	    			}
	    			return false;
	    		})

	    		var foo2 = [];
	    		if (deliverableTemplate.social_series.length > 0) {
	    			var foo2 = deliverableTemplate.social_series.filter((id) => {
	    				if (id == social_series_id) {
	    					return true;
	    				}
	    				return false;
	    			})
	    			if (foo2.length > 0) {
	    				return true;
	    			}
	    			return false;
	    		}


	    		if (foo.length > 0 && foo2.length > 0) {
	    			return true;
	    		}
	    		return false;
	    	}
	      return false;
	    })
	  }
	},


    getSocialMediaWithSocialCampaign:(state) => (social_campaign_id) => {
	  if (state.deliverableTemplates) {
	    return state.deliverableTemplates.filter((deliverableTemplate) => {
	    	if (typeof deliverableTemplate.social_campaigns != "undefined") {
		    	if (deliverableTemplate.social_campaigns.length > 0) {
		    		var foo = deliverableTemplate.social_campaigns.filter((id) => {
		    			if (id == social_campaign_id) {
		    				if (deliverableTemplate.social_series.length == 0) {
		    					return true;
		    				}
		    			}
		    			return false;
		    		})
		    		if (foo.length > 0) {
		    			return true;
		    		}
		    		return false;
		    	}
		    }
	      return false;
	    })
	  }
	},

	getSocialMediaWithSocialSeries:(state) => (social_series_id) => {
	  if (state.deliverableTemplates) {
	    return state.deliverableTemplates.filter((deliverableTemplate) => {
	    	if (typeof deliverableTemplate.social_series != "undefined") {
		    	if (deliverableTemplate.social_series.length > 0) {
		    		var foo = deliverableTemplate.social_series.filter((id) => {
		    			if (id == social_series_id) {
		    				return true;
		    			}
		    			return false;
		    		})
		    		if (foo.length > 0) {
		    			return true;
		    		}
		    		return false;
		    	}
		    }
	      return false;
	    })
	  }
	},




    projectDeliverableTemplates: state => state.projectDeliverableTemplates,
    allDeliverableTemplatesPaginationData: state => state.allDeliverableTemplatesPaginationData,
    allProjectsPaginationData: state => state.allProjectsPaginationData,
    allUserProjectsPaginationData: state => state.allUserProjectsPaginationData,
    allProjectDeliverableTemplatesPaginationData: state => state.allProjectDeliverableTemplatesPaginationData,
    userDeliverableTemplatesPaginationData: state =>
        state.userDeliverableTemplatesPaginationData,
    allCustomFields: state => state.customFields,
    userDeliverableTemplates: state => state.userDeliverableTemplates,
    unGroupedUserDeliverableTemplates: state => state.unGroupedUserDeliverableTemplates,
    deliverableTemplateContactsLabel: state => deliverableTemplate_template_id => {
        let label = "Who";
        switch (deliverableTemplate_template_id) {
            case 1:
                // Media Call
                label = "Outlets";
                break;
        }
        return label;
    },
    deliverableTemplateContactLabel: state => deliverableTemplate_template_id => {
        let label = "Who";
        switch (deliverableTemplate_template_id) {
            case 1:
                // Media Call
                label = "Outlet";
                break;
        }
        return label;
    },
    deliverableTemplateContactCompanyLabel: state => deliverableTemplate_template_id => {
        let label = "Company";
        switch (deliverableTemplate_template_id) {
            case 1:
                // Media Call
                label = "Outlet";
                break;
        }
        return label;
    },
    deliverableTemplateTask: state => deliverableTemplate_template_id => {
        let label = "Company";
        switch (deliverableTemplate_template_id) {
            case 1:
                // Media Call
                label = "Outlet";
                break;
        }
        return label;
    },
    allUsers: state => {
        let users = [];
        let isArchived = '';
        if (state.users) {
            state.users.forEach(function(item, index) {
                isArchived = !item.is_enabled ? ' (archived)' : '';
                users.push({ id: item.id, name: item.name+isArchived, userRoles: item.userRoles });
            });
        }

        return users;
    },
    allActiveUsers: state => state.activeUsers,
    allCountries: state => {
        let countries = [];
        state.countries.forEach(country => {
            countries.push(country.name);
        });
        return countries;
    },
    allRegions: state => {
        let regions = [];
        state.regions.forEach(region => {
            regions.push(region.name);
        });
        return regions;
    },
    allStates: state => {
        let countryStates = [];
        state.countryStates.forEach(countryState => {
            countryStates.push(countryState.name);
        });
        return countryStates;
    },
    allCompanies: state => {
        return state.allCompanies;
    },
    allContacts: state => {
        let contacts = [];
        state.contacts.forEach(function(item, index) {
            let obj = item;
            obj.name = item.first_name + " " + item.last_name;
            obj.commaName = item.last_name + ", " + item.first_name;
            contacts.push(item);
            // contacts.push({
            //     id: item.id,
            //     name: item.first_name + " " + item.last_name
            // });
        });
        return contacts;
    },
    allActiveContacts: state => {
        let contacts = [];
        state.contacts.forEach(function(item, index) {
            let obj = item;
            obj.name = item.first_name + " " + item.last_name;
            obj.commaName = item.last_name + ", " + item.first_name;
            contacts.push(item);
            // contacts.push({
            //     id: item.id,
            //     name: item.first_name + " " + item.last_name
            // });
        });
        return contacts;
    },
    allDepartments: state => {
        let departments = [];
        state.departments.forEach(function(item, index) {
            departments.push({ id: item.id, name: item.title });
        });
        return departments;
    },
    allActiveDepartments: state => {
        let entries = [];
        state.activeDepartments.forEach(function(item, index) {
            entries.push({ id: item.id, name: item.title });
        });
        return entries;
    },
    allSocialSeries: state => {
        let entries = [];
        state.socialSeries.forEach(function(item, index) {
            entries.push({ id: item.id, name: item.title, social_campaign_id:item.social_campaign_id,engagement_manager: item.engagement_manager, project_lead: item.project_lead});
        });
        return entries;
    },

    allUserManageSocialCampaigns: state => {
        let entries = [];
        state.userManageSocialCampaigns.forEach(function(item, index) {
            entries.push({ id: item.id, name: item.title,engagement_manager: item.engagement_manager, project_lead: item.project_lead });
        });
        return entries;
    },

    allSocialCampaigns: state => {
        let entries = [];
        state.socialCampaigns.forEach(function(item, index) {
            entries.push({ id: item.id, name: item.title,engagement_manager: item.engagement_manager, project_lead: item.project_lead });
        });
        return entries;
    },
    allTags: state => {
        let tags = [];
        state.tags.forEach(function(item, index) {
            tags.push({ id: item.id, name: item.tag });
        });
        return tags;
    },
    allSubjects: state => {
        let subjects = [];
        state.subjects.forEach(function(item, index) {
            subjects.push({ id: item.id, name: item.title });
        });
        return subjects;
    },
    deliverableTemplateDetail: state => state.deliverableTemplateDetail,
    projectDetail: state => state.projectDetail,
    dataLoading: state => state.dataLoading,
    deliverableTemplateCount: state => state.deliverableTemplateCount,
    tasksToHighlight: state => state.tasksToHighlight,
    taskHighlighted: state => state.taskHighlighted,
    newProjectCreated: state => state.newProjectCreated,
    projectSort: state => state.projectSort,
    myProjectSort: state => state.myProjectSort,
    isDirty: state => state.isDirty,
    dashboardMediaCallsFilter: state => state.dashboardMediaCallsFilter,
    numberStat: state => state.numberStat,
    widgetSettings: state => state.widgetSettings,
    systemSettings: state => state.systemSettings,
    deliverableTemplatePublishStatus: state => state.deliverableTemplatePublishStatus,
    socialCampaignDetail: state => state.socialCampaignDetail,
    socialGroupBy: state => state.socialGroupBy, // what user last selected, group by date or campaign/series
    socialSeriesDetail: state => state.socialSeriesDetail,
};
let fetchProjectXhrCount = 0;
let fetchUserProjectXhrCount = 0;
let fetchDeliverableTemplateXhrCount = 0;
let fetchProjectDeliverableXhrCount = 0;
let fetchUserManageProjectXhrCount = 0;
const actions = {


	async showPostContentLangInListing({ commit, state }, payload) {
	    commit('showPostContentLangInListing', payload)
	},
	async showPostContentNetworkInListing({ commit, state }, payload) {
	    commit('showPostContentNetworkInListing', payload)
	},

    updateNewContactRelatedValues({ commit }, payload) {
        commit('updateNewContactRelatedValues', payload);
    },
    updateEditContactRelatedValues({ commit }, payload) {
        commit('updateEditContactRelatedValues', payload);
    },

    async saveTaskOrder({ commit, state }, payload) {
        commit('setDataLoading', true)
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'deliverableTemplates/saveTaskOrder',
            payload
        )
        commit('setDataLoading', false)
    },
    async saveTaskFieldOrder({ commit, state }, payload) {
        commit('setDataLoading', true)
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'deliverableTemplates/saveTaskFieldOrder',
            payload
        )
        commit('updateTaskFieldOrder', payload)
        commit('setDataLoading', false)
    },
    async updateDueDate({ commit }, payload) {
        if(payload.projectData.due_date) {
            var s = Vue.moment(payload.projectData.due_date);
                    s.set("hour", 0);
                    s.set("minute", 0);
                    s.set("second", 0);
                    payload.projectData.due_date = s.utc().format();
        }
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'deliverableTemplates/updateProjectDueDate',
            {projectDetail: payload.projectData}
        )
        commit('setProjectDetail', response.data)
    },
    async getProjectNumberFromId({ commit }, projectId) {
        let response = await axios.get(
            process.env.MIX_API_BASE_URL + 'deliverableTemplates/getProjectNumberFromId/' + projectId
        )
        return response.data;
    },
    async createNewField({ commit }, payload) {
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'createNewField',
            payload
        );
        return response
        // commit('setDeliverableTypes', response.data)
    },
    async removeCustomField({ commit }, payload) {
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'deliverableTemplates/removeCustomField',
            payload
        )
        let deliverableTemplateData = response.data.deliverableTemplateData
        let deliverableTemplateId = payload.deliverableTemplateId
        commit('removeFieldFromAllTasks', payload.labelId)
        commit('updateTaskData', {deliverableTemplateData, deliverableTemplateId})
        commit('groupUserDeliverable')
    },
    async updateFieldLabel({ commit }, payload) {
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'deliverableTemplates/updateFieldLabel',
            payload
        )

        commit('updateFieldLabelText', response.data)
    },

    async getRouterNameFromLocationHref({ commit, getters }) {
    	let url = window.location.pathname;

    	// looking at a social media post within the scheduled content tab of an organic social media plan.
		if (url.indexOf("/project/deliverableTemplate/") != -1 && url.indexOf("/social-media-schedule/") != -1) {
			return "socialMediaSchedule";
		}

		return router.currentRoute.name;
    },
    async getDeliverableCalendarData({ commit }, deliverableTemplateId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableCalendarData/" +
                deliverableTemplateId
        );
        return typeof response.data == "undefined" ? response : response.data
    },
    async getCalendarData({ commit, getters }, payload) {
        let dataSetType = typeof payload == 'undefined' ? 'deliverableTemplates' : payload
        let filter = {},
        currentComponent = "calendar";
        filter = JSON.parse(JSON.stringify(getters.calendarFilter));
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + 'getCalendarData?dataSetType='+dataSetType+'&currentComponent=' + currentComponent,
            filter
        );
        //TODO:: append data or update data
        commit('setCalendarEvents', response.data)
        return response.data;
    },
    async getDeliverableTemplates({ commit, dispatch }, payload) {
        let response = await axios.get(
            process.env.MIX_API_BASE_URL + 'getDeliverableTemplates',
        );
        // commit('setDeliverableTemplates', response.data.preferenceDeliverableTemplates)
        await commit('setAllDeliverableTemplates', response.data.allDeliverableTemplates.data)
        // const newDeliverableTemplates = await dispatch("getTemplateableDeliverableTemplates");
        // commit('setNewDeliverableTemplates', newDeliverableTemplates);
    },

    async filterDeliverableTemplatesBasedOnSettings({state, commit, dispatch }) {
        // Check if state.deliverableTemplates is empty, then fetch the data
        if (state.allDeliverableTypes.length === 0) {
            await dispatch("getDeliverableTemplates");
        }
        const newDeliverableTypes = await dispatch("getRevisedDeliverableTemplates");
        commit('setNewDeliverableTemplates', newDeliverableTypes);
    },
    async filterNewDeliverableTemplatesBasedOnSettings({state, commit, dispatch }) {
        // Check if state.deliverableTemplates is empty, then fetch the data
        if (state.allDeliverableTypes.length === 0) {
            await dispatch("getDeliverableTemplates");
        }
        // const newDeliverableTypes = await dispatch("getRevisedDeliverableTemplates");
        // commit('setNewDeliverableTemplates', newDeliverableTypes);
    },
    async getRevisedDeliverableTemplates({ state, dispatch, getters }) {
        const showSocialMediaInPlansOnlySetting = getters.getSystemSetting('social_media___post_requires_plan');
        if (showSocialMediaInPlansOnlySetting) {
            return state.deliverableTemplates.filter(item => item.id !== 'social_media') || state.deliverableTemplates;
        } else {
            return state.deliverableTemplates;
        }
    },
    async getTemplateableDeliverableTemplates({ state, dispatch, getters }) {
        return state.deliverableTemplates.filter(item => item.can_be_templated == 1) || state.deliverableTemplates;
    },

    async getSocialMediaInPlansOnlyFromSettings({ state }, keyToCheck) {
    	if (state.systemSettings.settings) {
	        const socialMediaSetting = state.systemSettings.settings.find(setting => setting.setting_key === keyToCheck);
	        return socialMediaSetting?.setting_value?.value || false;
    	}
    	return false;
    },
    async getSocialMediaInPlansOnlyFromSettings({ state }, keyToCheck) {
    	if (state.systemSettings.settings) {
	        const socialMediaSetting = state.systemSettings.settings.find(setting => setting.setting_key === keyToCheck);
	        return socialMediaSetting?.setting_value?.value || false;
    	}
    	return false;
    },
    async getPreferenceDeliverableTypes({ commit }, payload) {
        let response = await axios.get(
            process.env.MIX_API_BASE_URL + 'getPreferenceDeliverableTypes',
        );
        commit('setPreferenceDeliverableTypes', response.data)
    },
    async idToLabel({}, payload) {
        let response = await axios.get(
            process.env.MIX_API_BASE_URL + 'idToLabel',
            {params: payload}
        );
        return response.data;
    },
    async fetchAuditLogs({ commit, getters }, payload) {
        let page = '';
        let filter = getters.auditLogFilter;
        if(typeof payload != 'undefined' && typeof payload.page != 'undefined') {
            page = '?page='+payload.page;
        }
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'fetchAuditLogs' + page,
            filter
        );
        commit('setAuditLogs', response.data);
        return response.data;
    },
    manageUndoActivation({ commit }, text) {
        if(typeof text == 'undefined') {
            text = 'Action has been completed.';
        }
        if(typeof window.undoInterval != 'undefined') {
            commit('setActivateUndo', false);
            clearInterval(window.undoInterval);
        }
        commit('setActivateUndoText', text);
        commit('setActivateUndo', true);
        let sec = 10; // seconds to show the undo dialog
        $('.sec').text(sec);
        window.undoInterval = setInterval(function() {
            sec--;
            $('.sec').text(sec);
            if(sec == 0) {
                commit('setActivateUndo', false);
                clearInterval(window.undoInterval);
            }
        }, 1000)
    },
    manageUndoActivationNoScroll({ commit, dispatch }, text) {
        dispatch("manageUndoActivation", text);
        commit("setActivateUndoNoScroll", true);
    },
    manageProjectUndoActivation({ commit }, id) {
        if(typeof window.projectUndoInterval != 'undefined') {
            commit('setActivateMyProjectUndo', false);
            clearInterval(window.projectUndoInterval);
        }
        commit('setActivateMyProjectUndo', true);
        commit('setActivateMyProjectUndoId', id);
        let sec = 10;
        $(".sec-project").text(sec);
        window.projectUndoInterval = setInterval(function() {
            sec--;
            $(".sec-project").text(sec);
            if (sec == 0) {
                commit('setActivateMyProjectUndo', false);
                clearInterval(window.projectUndoInterval);
            }
        }, 1000);
    },
    async undoAction({ commit, dispatch, state }) {
        let currentComponent = router.currentRoute.name;
        let response = await axios.put(
            process.env.MIX_API_BASE_URL + 'undoAuditLog?currentComponent=' + currentComponent
        );
        if(
            currentComponent == 'dashboard' || 
            currentComponent == 'myWeek'
        ){
            commit(
                "setDeliverableTemplates",
                typeof response.data == "undefined" ? response : response.data
            );
        } else if(
            currentComponent == 'socialMedia' || 
            currentComponent == 'socialMediaGrouped'
        ){
            commit('setDeliverableTemplates', typeof response.data == "undefined" ? response : response.data);
            if(typeof response.data.allSocialSeries != 'undefined') {
                commit('setListSocialSeries', typeof response.data == "undefined" ? response.allSocialSeries : response.data.allSocialSeries);
            }
            if(typeof response.data.allSocialCampaigns != 'undefined') {
                commit('setListSocialCampaigns', typeof response.data == "undefined" ? response.allSocialCampaigns : response.data.allSocialCampaigns);
            }
        } else if(currentComponent == 'socialMediaSchedule'){
            commit("setCalendarDeliverableTemplates", response.data);
        } else if(currentComponent == 'projectDeliverable'){
            // commit("setProjectDetail", response.data.projectDetail);
            // commit("setProjectDeliverableTemplates", response.data);
            dispatch('fetchProjectDeliverable', state.currentPage);
        } else if(currentComponent == 'project') {
            commit("setProjects", response.data);
        } else if(currentComponent == 'myProjects') {
            dispatch('fetchUserProjectsNoScroll', state.currentPage);
        }

        if (state.activateUndoNoScroll) {
            commit("setActivateUndoNoScroll", false);
        } else {
            window.scrollTo(0, 0);
        }
    },
    async fetchAllCompanies({ commit }) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "getAllCompanies",
        );
        commit("setAllCompanies", response.data);
    },
    async fetchProjectDeliverable({ commit, getters }, page) {
        let sequenceNumber = ++fetchProjectDeliverableXhrCount;
        let filter = getters.projectDeliverableListFilter
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }
        this.state.currentPage = page;
        /*
        commit(
            "resetProjectDetail"
        )
        commit(
            "resetProjectDeliverableTemplates"
        )
        */
        if (
            typeof router.currentRoute.params.projectId != "undefined" && 
            router.currentRoute.params.projectId != "undefined"
        ) {
	        const response = await axios.post(
	            process.env.MIX_API_BASE_URL +
	                "getProjectDeliverable/"+router.currentRoute.params.projectId+"?page=" +
	                page,
	            filter
	        );
	        if (
	            typeof response.data.allDeliverableTemplates != "undefined" &&
	            response.data.allDeliverableTemplates.to -
	                response.data.allDeliverableTemplates.from ==
	                1
	        ) {
	            this.state.currentPage -= 1;
	        }

	        if(sequenceNumber == fetchProjectDeliverableXhrCount) {
	            fetchProjectDeliverableXhrCount = 0;
	            commit(
	                "setProjectDeliverableTemplates",
	                typeof response.data == "undefined" ? response : response.data
	            );
	            commit(
	                "setProjectDetail",
	                response.data.projectDetail
	            )
	            // window.scrollTo(0, 0);
	        }
        	return response;
	    } else {
	    	return null;
	    }
    },
    async fetchProjectCalendarDeliverable({ commit, getters }, page) {
        let filter = JSON.parse(JSON.stringify(getters.projectCalendarFilter));
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }
        this.state.currentPage = page;
        if (
            typeof router.currentRoute.params.projectId != "undefined" &&
            router.currentRoute.params.projectId != "undefined"
        ) {
            const response = await axios.post(
                process.env.MIX_API_BASE_URL +
                    "getProjectCalendarDeliverable/" +
                    router.currentRoute.params.projectId,
                filter
            );
            commit(
                "setProjectCalendarEvents",
                typeof response.data == "undefined" ? response : response.data
            );
            return response.data;
        } else {
            return null;
        }
    },
    async fetchProjectDeliverableById({ commit, getters }, id) {
        let sequenceNumber = ++fetchProjectDeliverableXhrCount;

        const defaultFilter = {
            query: "",
            sort: '',
            multiSort: [],
            priorityFilter: 0,
            outletCompany: [],
            filterByDate: false,
            filterByDateCreated: false,
            filterByDateCompleted: false,
            dateRange: {
                startDate: new Date(),
                endDate: new Date()
            },
            dateCreatedRange: {
                startDate: new Date(),
                endDate: new Date()
            },
            dateCompletedRange: {
                startDate: new Date(),
                endDate: new Date()
            },
            status: [],
            outlet: [],
            deliverableTemplate: [],
            tag: [],
            department: [],
            coordinator: [],
            noDeparment: 'not_accepted',
            noOutlet: 'not_accepted',
        }
        const page = 1;

        if (typeof id !== 'undefined') {
            const response = await axios.post(
	            process.env.MIX_API_BASE_URL +
	                "getProjectDeliverable/"+id+"?page=" +
	                page,
                    defaultFilter
	        );
	        if (
	            typeof response.data.allDeliverableTemplates != "undefined" &&
	            response.data.allDeliverableTemplates.to -
	                response.data.allDeliverableTemplates.from ==
	                1
	        ) {
	            this.state.currentPage -= 1;
	        }

            if(sequenceNumber == fetchProjectDeliverableXhrCount) {
	            fetchProjectDeliverableXhrCount = 0;
	            commit(
	                "setProjectDeliverableTemplates",
	                typeof response.data == "undefined" ? response : response.data
	            );
	            commit(
	                "setProjectDetail",
	                response.data.projectDetail
	            )
	            // window.scrollTo(0, 0);
	        }
            return response;
        } else {
            return null;
        }

    },
    async fetchProjectUnreadMessageCount({ state, commit, getters }, type) {
        let existingProjectData;
        if(type=="project") {
            existingProjectData = getters.projects;
        } else if(type=="my-projects") {
            existingProjectData = getters.userProjects;
        }
        const projectIdArray = existingProjectData.map(item => item.id);
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getProjectsUnreadMessageCount",
                { params: { projectIdArray } }
        );
        const newProjectData = response.data.map(obj => ({
            ...obj,
            id: parseInt(obj.id, 10) // Convert id to number using parseInt
          }));
        //set count into each row of existingProjectData in store
        const mergedUserProjectsArray = existingProjectData.map(item1 => {
            let matchingItem = newProjectData.find(item2 => item2.id === item1.id);
            return {...item1, ...matchingItem};
        });
        if (JSON.stringify(existingProjectData) !== JSON.stringify(mergedUserProjectsArray)) {
            if(type=="project") {
                commit(
                    "setProjectsOnly",
                    mergedUserProjectsArray
                );
            } else if(type=="my-projects") {
                commit(
                    "setUserProjectsOnly",
                    mergedUserProjectsArray
                );
            }
        }
    },

    async fetchUserProjects({ state, commit, getters }, page) {
        let sequenceNumberUserProject = ++fetchUserProjectXhrCount;
        let filter = {};

        filter = JSON.parse(JSON.stringify(getters.myProjectFilter));
        filter.sort = getters.myProjectSort;

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }
        state.currentPage = page;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getUserProjects?page=" +
                page,
            filter
        );
        if (
            typeof response.data.allDeliverableTemplates != "undefined" &&
            response.data.allDeliverableTemplates.to -
                response.data.allDeliverableTemplates.from ==
                1
        ) {
            state.currentPage -= 1;
        }

        if(sequenceNumberUserProject == fetchUserProjectXhrCount) {
            fetchUserProjectXhrCount = 0;
            commit(
                "setUserProjects",
                typeof response.data == "undefined" ? response : response.data
            );
            window.scrollTo(0, 0);
        }
    },
    async fetchUserProjectsNoScroll({ dispatch }, page) {
        const yOffset = window.pageYOffset;
        const result = await dispatch("fetchUserProjects", page);
        window.scrollTo(0, yOffset);
        return result;
    },
    async fetchUserProjectNoSet({ state, commit, getters }, page) {
        let filter = {};

        filter = JSON.parse(JSON.stringify(getters.myProjectFilter));

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = state.currentPage;
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getUserProjects?page=" +
                page,
            filter
        );

        return response;
    },
    async fetchUserProjectsCount({ commit, getters }) {
        if (!getters.isLoggedIn) return 0;

        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
            "getUserProjectsCount"
        );

        if (response.data) {
            commit('setUserHasProjects', response.data.count);
        } else {
            commit('setUserHasProjects', response);
        }
    },
    async fetchUserManageProjects({ commit, getters }) {
        let sequenceNumberUserManageProject = ++fetchUserManageProjectXhrCount;
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
            "getUserManageProjects"
        );

        if (sequenceNumberUserManageProject == fetchUserManageProjectXhrCount) {
            fetchUserManageProjectXhrCount = 0;

            commit(
                "setUserManageProjects",
                typeof response.data == "undefined" ? response : response.data
            );
        }
    },
    async fetchUserManageSocialCampaigns({ commit, getters }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
            "getUserManageSocialCampaigns"
        );
        commit(
            "setUserManageSocialCampaigns",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async fetchProjects({ state, commit, getters }, page) {
        let sequenceNumberProject = ++fetchProjectXhrCount;
        let filter = {};

        filter = JSON.parse(JSON.stringify(getters.projectFilter));
        filter.sort = getters.projectSort;

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }
        state.currentPage = page;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getProjects?page=" +
                page,
            filter
        );
        if (
            typeof response.data.allDeliverableTemplates != "undefined" &&
            response.data.allDeliverableTemplates.to -
                response.data.allDeliverableTemplates.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }

        if(sequenceNumberProject == fetchProjectXhrCount) {
            fetchProjectXhrCount = 0;
            commit(
                "setProjects",
                typeof response.data == "undefined" ? response : response.data
            );
            window.scrollTo(0, 0);
        }
    },
    async fetchProjectsNoScroll({ dispatch }, page) {
        const yOffset = window.pageYOffset;
        const result = await dispatch("fetchProjects", page);
        window.scrollTo(0, yOffset);
        return result;
    },
    async fetchAllProjects({ commit, getters }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getAllProjects"
        );

        commit("setAllProjects", response.data);
    },


    async fetchUserDeliverableUnreadMessageCount({ state, commit, getters }) {
        let existingDeliverableData;
        let currentComponent = router.currentRoute.name;
        let deliverableTemplateIdArray={};
        if (currentComponent == "myWeek") {
            existingDeliverableData = getters.userDeliverableTemplates;
            Object.keys(existingDeliverableData).forEach(function(key,index) {
                deliverableTemplateIdArray[key] = existingDeliverableData[key].map(item => item.id);
            });
            const response = await axios.get(
                process.env.MIX_API_BASE_URL +
                    "getUserDeliverableTemplatesUnreadMessageCount",
                    { params: { deliverableTemplateIdArray } }
            );
            if (typeof response.data != "undefined" ) {
                let newDeliverableTemplatesData = {};
                Object.keys(response.data).forEach(function(key,index) {
                    newDeliverableTemplatesData[key] = response.data[key].map(obj => ({
                        ...obj,
                        id: parseInt(obj.id, 10) // Convert id to number using parseInt
                    }));
                });
                //set count into each row of existingDeliverableData in store
                const mergedDeliverableData = {};
                Object.keys(existingDeliverableData).forEach(key => {
                    // Check if key exists in newData
                    if (newDeliverableTemplatesData.hasOwnProperty(key)) {
                         // Merge arrays if key exists in both objects
                         mergedDeliverableData[key]  = existingDeliverableData[key].map(item1 => {
                            let matchingItem = newDeliverableTemplatesData[key].find(item2 => item2.id === item1.id);
                            return {...item1, ...matchingItem};
                        });
                    } else {
                        // Copy array if key exists only in existingData
                        mergedDeliverableData[key] = existingDeliverableData[key].slice();
                    }
                });
                if (JSON.stringify(existingDeliverableData) !== JSON.stringify(mergedDeliverableData)) {
                    commit(
                        "setGroupedUserDeliverableTemplatesOnly",
                        mergedDeliverableData
                    );
                }
            }
        }
    },
    async fetchDeliverableUnreadMessageCount({ state, commit, getters }) {
        let existingDeliverableData;
        let currentComponent = router.currentRoute.name;
        if (currentComponent == "mediaCallPage" || currentComponent == "dashboard") {
            existingDeliverableData = getters.allDeliverableTemplates;
        } else if (currentComponent == "projectDeliverable") {
            existingDeliverableData = getters.projectDeliverableTemplates;
        } else {
            return;
        }
        const deliverableTemplateIdArray = existingDeliverableData.map(item => item.id);
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableTemplatesUnreadMessageCount",
                { params: { deliverableTemplateIdArray } }
        );
        if (typeof response.data != "undefined" ) {
            const newDeliverableData = response.data.map(obj => ({
                ...obj,
                id: parseInt(obj.id, 10) // Convert id to number using parseInt
              }));
            //set count into each row of existingProjectData in store
            const mergedDeliverableArray = existingDeliverableData.map(item1 => {
                let matchingItem = newDeliverableData.find(item2 => item2.id === item1.id);
                return {...item1, ...matchingItem};
            });
            if (JSON.stringify(existingDeliverableData) !== JSON.stringify(mergedDeliverableArray)) {
                if (currentComponent == "mediaCallPage" || currentComponent == "dashboard") {
                    commit(
                        "setDeliverableTemplatesOnly",
                        mergedDeliverableArray
                    );
                } else if (currentComponent == "projectDeliverable") {
                    commit(
                        "setProjectDeliverableTemplatesOnly",
                        mergedDeliverableArray
                    );
                } 

            }
        }
    },

    async fetchDeliverableBreadcrumbs({ commit }, payload) {
        try {
          const response = await axios.get(
            process.env.MIX_API_BASE_URL +
            "getDeliverableBreadcrumbs",
            {
              params: {
                ...payload,
              },
            });
            if (response.data && response.data.data) {
                commit('setBreadcrumbs', response.data.data);
            }
        } catch (error) {
          console.error("Error fetching breadcrumb data:", error);
        }
    },

    async fetchDeliverableTemplates({ state, commit, getters, dispatch }, page) {
      let sequenceNumber = ++fetchDeliverableTemplateXhrCount;
      let filter = {},

      currentComponent = "deliverableTemplate-templates";

      // if (router.currentRoute.name == "myWeek") {
      //     filter = JSON.parse(
      //         JSON.stringify(getters.myWeekDeliverableFilter)
      //     );
      //     currentComponent = "myweek";
      // } else if (router.currentRoute.name == "projectDeliverable") {
      //     filter = JSON.parse(
      //         JSON.stringify(getters.addProjectDeliverableFilter)
      //     );
      //     currentComponent = "projectDeliverable";
      // } else if (router.currentRoute.name == "mediaCallPage") {
      //     filter = JSON.parse(JSON.stringify(getters.mediaCallFilter));
      //     currentComponent = "mediaCallPage";
      // } else if (router.currentRoute.name == "socialMedia") {
      //     filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
      //     currentComponent = "socialMedia";
      // } else if (router.currentRoute.name == "socialMediaSchedule") {
      //     filter = JSON.parse(JSON.stringify(getters.socialMediaCalendarFilter));

      //     // currentComponent = "socialMediaCalendarPage";
      //     // Using the component "socialMedia" because the different structure of the return data of "socialMediaCalendarPage" will cause error
      //     // And also we don't really use this for the calendar page anyways but something keep calling this function
      //     // --- Indy
      //     currentComponent = "socialMedia";
      // } else {
          filter = JSON.parse(JSON.stringify(getters.deliverableTemplateFilter));


          // if (typeof filter.parentDeliverableId  != "undefined") {
          //     if(filter.parentDeliverableId != 0) {
          //         // for organic social media plan
          //         filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
          //     } 
          // }

      // }


      if (filter.filterByDate == true) {
          // var s = Vue.moment(filter.dateRange.startDate);
          // s.set('hour', 0);
          // s.set('minute', 0);
          // s.set('second', 0);
          // filter.dateRange.startDate = s.toISOString();
          // var e = Vue.moment(filter.dateRange.endDate);
          // e.set('hour', 23);
          // e.set('minute', 59);
          // e.set('second', 59);
          // filter.dateRange.endDate = e.toISOString();
      }
      if (filter.filterByDateCreated == true) {
          var s = Vue.moment(filter.dateCreatedRange.startDate);
          s.set("hour", 0);
          s.set("minute", 0);
          s.set("second", 0);
          filter.dateCreatedRange.startDate = s.utc().format();
          var e = Vue.moment(filter.dateCreatedRange.endDate);
          e.set("hour", 23);
          e.set("minute", 59);
          e.set("second", 59);
          filter.dateCreatedRange.endDate = e.utc().format();
      }
      if (filter.filterByDateCompleted == true) {
          var s = Vue.moment(filter.dateCompletedRange.startDate);
          s.set("hour", 0);
          s.set("minute", 0);
          s.set("second", 0);
          filter.dateCompletedRange.startDate = s.utc().format();
          var e = Vue.moment(filter.dateCompletedRange.endDate);
          e.set("hour", 23);
          e.set("minute", 59);
          e.set("second", 59);
          filter.dateCompletedRange.endDate = e.utc().format();
      }

      if (typeof filter.filterByPublishDate != "undefined") {
        if (filter.filterByPublishDate == true) {
            var s = Vue.moment(filter.datePublishRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.datePublishRange.startDate = s.utc().format();
            var e = Vue.moment(filter.datePublishRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.datePublishRange.endDate = e.utc().format();
        }
    }

      // if (currentComponent != "socialMedia") {
        if (typeof filter.listByPublishDate  != "undefined") {
          filter.listByPublishDate = false;
        }
        // filter.socialCampaign = [];
        // filter.socialSeries = [];
      // }

      //kevin
      let filter2 = JSON.parse(
          JSON.stringify(getters.deliverableTemplateCount)
      );
      filter.pageCount = 18;
      if (typeof page == "undefined") {
          page = 1;
      }
      let overdueCount = filter2.overdue;
      let todayCount = filter2.today;
      let thisWeekCount = filter2.thisWeek;
      let nextWeekCount = filter2.nextWeek;
      let upcomingCount = filter2.upcoming;
      let noDeadlineCount = filter2.noDeadline;
      if (page == 2){
          filter.pageCount = 35;
      }
      filter.overdueCount = overdueCount;
      filter.todayCount = todayCount;
      filter.thisWeekCount = thisWeekCount;
      filter.nextWeekCount = nextWeekCount;
      filter.upcomingCount = upcomingCount;
      filter.noDeadlineCount = noDeadlineCount;
      state.currentPage = page;
      const response = await axios.post(
          process.env.MIX_API_BASE_URL +
              "getDeliverableTemplates?page=" +
              page +
              "&currentComponent=" +
              currentComponent,
          filter
      );

      if (
          typeof response.data.allDeliverableTemplates != "undefined" &&
          response.data.allDeliverableTemplates.to -
              response.data.allDeliverableTemplates.from ==
              1
      ) {
          this.state.currentPage -= 1;
      }

      // if(typeof response.data.allDeliverableTemplates.outletCount != 'undefined') {
      //     commit('setOutletCount', response.data.allDeliverableTemplates.outletCount)
      // }

      if(sequenceNumber == fetchDeliverableTemplateXhrCount) {
          fetchDeliverableTemplateXhrCount = 0;
          // if (router.currentRoute.name != "myWeek" && router.currentRoute.name != "projectDeliverable") {
              dispatch("countOutlets");
          // }
          commit(
              "setDeliverableTemplates",
              typeof response.data == "undefined" ? response : response.data
          );
          if(currentComponent != 'projectDeliverable') {
              window.scrollTo(0, 0);
          }
      }
    },

    async fetchPartialCalendarDeliverableTemplates({ state, commit, getters }, filterQuery) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "getPartialCalendarDeliverableTemplates",
            filterQuery
        );
        if(typeof response.data != "undefined" )
        {
            if(filterQuery.mode == "year") {
                commit("setYearCalendarDeliverableTemplates", { 
                    allDeliverableTemplates: {
                        deliverableTemplates: {
                            data: response.data
                        }
                    } 
                });
            } else {
                // If year do not commit response
                commit("setCalendarDeliverableTemplates", { 
                    allDeliverableTemplates: {
                        deliverableTemplates: {
                            data: response.data
                        }
                    } 
                });
            }
        }
        return response.data;
    },

    async fetchCalendarDeliverableTemplates({ state, commit, getters }, page) {
        let filter = {};
        let currentComponent = "";
        page = 1;
        if (router.currentRoute.name == "socialMediaSchedule") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaCalendarFilter));
            currentComponent = "socialMediaCalendarPage";
        }

        if (typeof filter != "undefined") {
            if(filter.parentDeliverableId != 0) {
                // for organic social media plan
                filter = JSON.parse(JSON.stringify(getters.socialMediaCalendarFilter));
            } 
        }
        
        // May need to add in additional code for it to work properly, see fetchDeliverableTemplates
        if (filter.filterByPublishDate == true) {
            var s = Vue.moment(filter.datePublishRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.datePublishRange.startDate = s.utc().format();
            var e = Vue.moment(filter.datePublishRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.datePublishRange.endDate = e.utc().format();
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getDeliverableTemplatesCalendar?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );

        if (
            typeof response.data.allDeliverableTemplates != "undefined" &&
            response.data.allDeliverableTemplates.to -
                response.data.allDeliverableTemplates.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }

        commit(
            "setCalendarDeliverableTemplates",
            typeof response.data == "undefined" ? response : response.data
        );
    },


    async startDownloadDeliverableExcel({ state, commit, getters }, filterQuery) {
        let filter = {};
        let currentComponent = "";
        let page = 1;

        if (filterQuery) {
        	filter = filterQuery;
        } else {
	        if (router.currentRoute.name == "socialMediaSchedule") {
	            filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
	            currentComponent = "socialMediaCalendarPage";
	        }
	    }
        // May need to add in additional code for it to work properly, see fetchDeliverableTemplates
        if (filter.filterByPublishDate == true) {
            var s = Vue.moment(filter.datePublishRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.datePublishRange.startDate = s.utc().format();
            var e = Vue.moment(filter.datePublishRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.datePublishRange.endDate = e.utc().format();
        }

        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "exportSocialMedia?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );
        return response;
    },


    async updateCalendarDeliverablePublishDate({ state, commit, getters }, data) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "updateDeliverablePublishDate",
            data
        );
        
        const calendarDeliverableTemplates = getters.allCalendarDeliverableTemplates;
        const targetDeliverable = calendarDeliverableTemplates.find(x => x.id == response.data.deliverableTemplateDetail.id);
        if (targetDeliverable) {
        targetDeliverable.publish_date = response.data.deliverableTemplateDetail.publish_date;
        targetDeliverable.publish_status = response.data.deliverableTemplateDetail.publish_status;
        }

        commit("setCalendarDeliverableTemplates", { 
            allDeliverableTemplates: {
                deliverableTemplates: {
                    data: calendarDeliverableTemplates
                }
            } 
        });

		commit("setDeliverableTemplateDetailPublishDetails", response.data.deliverableTemplateDetail);



    },

    async undoUpdateDeliverablePublishDate({ state, commit, getters }, data) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "undoUpdateDeliverablePublishDate"
        );

        const calendarDeliverableTemplates = getters.allCalendarDeliverableTemplates;
        const targetDeliverable = calendarDeliverableTemplates.find(x => x.id == response.data.deliverableTemplateDetail.id);
        targetDeliverable.publish_date = response.data.deliverableTemplateDetail.publish_date;
        targetDeliverable.publish_status = response.data.deliverableTemplateDetail.publish_status;

        commit("setCalendarDeliverableTemplates", { 
            allDeliverableTemplates: {
                deliverableTemplates: {
                    data: calendarDeliverableTemplates
                }
            } 
        });

		commit("setDeliverableTemplateDetailPublishDetails", response.data.deliverableTemplateDetail);

    },


    async fetchSocialMediaDeliverableTemplatesGrouped({ state, commit, getters, dispatch }, params) {


        let sequenceNumber = ++fetchDeliverableXhrCount;
        let filter = {},
            currentComponent = router.currentRoute.name;
        if (router.currentRoute.name == "myWeek") {
            filter = JSON.parse(
                JSON.stringify(getters.myWeekDeliverableFilter)
            );
        } else if (router.currentRoute.name == "projectDeliverable") {
            filter = JSON.parse(
                JSON.stringify(getters.addProjectDeliverableFilter)
            );
        } else if (router.currentRoute.name == "mediaCallPage") {
            filter = JSON.parse(JSON.stringify(getters.mediaCallFilter));
        } else if (router.currentRoute.name == "socialMedia") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
        } else if (router.currentRoute.name == "socialMediaSchedule") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaCalendarFilter));
        } else if (router.currentRoute.name == "socialMediaGrouped") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
            currentComponent = "socialMediaGroupPage";            
        } else {
            filter = JSON.parse(JSON.stringify(getters.deliverableTemplateFilter));
            currentComponent = "dashboard";

        }
        if (typeof filter.parentDeliverableId  != "undefined") {
            if(filter.parentDeliverableId != 0) {
                // for organic social media plan
                filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
            } 
        }
        if (typeof filter.pageCount == "undefined") {
        	filter.pageCount = 99999;
        }

        let page = 1;
        if (typeof params != "undefined") {
            if (typeof params.filter != "undefined") {
            	filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
            }
	        if (typeof params.page != "undefined") {
	        	page = params.page;
	        }
	        if (typeof params.pageCount != "undefined") {
	        	filter.pageCount = params.pageCount;
	        }
        }
        filter.page = page;


        if (filter.filterByDate == true) {
            // var s = Vue.moment(filter.dateRange.startDate);
            // s.set('hour', 0);
            // s.set('minute', 0);
            // s.set('second', 0);
            // filter.dateRange.startDate = s.toISOString();
            // var e = Vue.moment(filter.dateRange.endDate);
            // e.set('hour', 23);
            // e.set('minute', 59);
            // e.set('second', 59);
            // filter.dateRange.endDate = e.toISOString();
        }
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (filter.filterByPublishDate == true) {
            var s = Vue.moment(filter.datePublishRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.datePublishRange.startDate = s.utc().format();
            var e = Vue.moment(filter.datePublishRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.datePublishRange.endDate = e.utc().format();
        }

        let filter2 = JSON.parse(
            JSON.stringify(getters.deliverableTemplateCount)
        );


        // overdue , today , thisweek, nextweek, upcoming , overdue
        let overdueCount = filter2.overdue;
        let todayCount = filter2.today;
        let thisWeekCount = filter2.thisWeek;
        let nextWeekCount = filter2.nextWeek;
        let upcomingCount = filter2.upcoming;
        let noDeadlineCount = filter2.noDeadline;
        filter.overdueCount = overdueCount;
        filter.todayCount = todayCount;
        filter.thisWeekCount = thisWeekCount;
        filter.nextWeekCount = nextWeekCount;
        filter.upcomingCount = upcomingCount;
        filter.noDeadlineCount = noDeadlineCount;
        //kevin end
        filter.parentDeliverableId = filter.parentDeliverableId;
        state.currentPage = page;



        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getSocialMediaDeliverableTemplatesGrouped?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );
        if (
            typeof response.data.allDeliverableTemplates != "undefined" &&
            response.data.allDeliverableTemplates.to -
                response.data.allDeliverableTemplates.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }

        // if(typeof response.data.allDeliverableTemplates.outletCount != 'undefined') {
        //     commit('setOutletCount', response.data.allDeliverableTemplates.outletCount)
        // }



        if(sequenceNumber == fetchDeliverableXhrCount) {
            fetchDeliverableXhrCount = 0;
            if (router.currentRoute.name != "myWeek" && router.currentRoute.name != "projectDeliverable") {
                dispatch("countOutlets");
            }
            commit(state._namespace+"setDeliverableTemplates",typeof response.data == "undefined" ? response : response.data);


            if(currentComponent != 'socialMediaGrouped') {
                window.scrollTo(0, 0);
            }
        } else {
        }

        if (typeof response.data == "undefined" ) {
        	if(typeof response.allSocialSeries != 'undefined') {
        	    commit('setListSocialSeries', response.allSocialSeries);
        	} else {
				commit('setListSocialSeries', []);
        	}
        	if(typeof response.allSocialCampaigns != 'undefined') {
        	    commit('setListSocialCampaigns', response.allSocialCampaigns);
        	} else {
        	    commit('setListSocialCampaigns', []);
        	}
        } else {
	        if(typeof response.data.allSocialSeries != 'undefined') {
	            commit('setListSocialSeries', typeof response.data == "undefined" ? response.allSocialSeries : response.data.allSocialSeries);
	    	} else {
				commit('setListSocialSeries', []);
	    	}
	        if(typeof response.data.allSocialCampaigns != 'undefined') {
	            commit('setListSocialCampaigns', typeof response.data == "undefined" ? response.allSocialCampaigns : response.data.allSocialCampaigns);
	        } else {
        	    commit('setListSocialCampaigns', []);
        	}
	    }

    },


    async fetchPublicDeliverableTemplatesGraph({ state, commit, getters, dispatch }, page) {
        let sequenceNumber = ++fetchDeliverableXhrCount;
        let filter = {},
            currentComponent = "dashboardMediaCallsGraph";

        filter = JSON.parse(JSON.stringify(getters.dashboardMediaCallsFilter));

        currentComponent = filter.dashboardType;

        if (filter.filterByDate == true) {
        }
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        //kevin
        let filter2 = JSON.parse(
            JSON.stringify(getters.deliverableTemplateCount)
        );
        filter.pageCount = 18;
        if (typeof page == "undefined") {
            page = 1;
        }
        let overdueCount = filter2.overdue;
        let todayCount = filter2.today;
        let thisWeekCount = filter2.thisWeek;
        let nextWeekCount = filter2.nextWeek;
        let upcomingCount = filter2.upcoming;
        let noDeadlineCount = filter2.noDeadline;
        if (page == 2){
            filter.pageCount = 35;
        }
        filter.overdueCount = overdueCount;
        filter.todayCount = todayCount;
        filter.thisWeekCount = thisWeekCount;
        filter.nextWeekCount = nextWeekCount;
        filter.upcomingCount = upcomingCount;
        filter.noDeadlineCount = noDeadlineCount;
        state.currentPage = page;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "widget/getPublicDeliverableList?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );
        if (
            typeof response.data.allDeliverableTemplates != "undefined" &&
            response.data.allDeliverableTemplates.to -
                response.data.allDeliverableTemplates.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }


        if(sequenceNumber == fetchDeliverableXhrCount) {
            fetchDeliverableXhrCount = 0;
            if(currentComponent != 'projectDeliverable') {
                window.scrollTo(0, 0);
            }
            return response.data;
        }
        return response.data;
    },

    async fetchPublicDeliverableTemplates({ state, commit, getters, dispatch }, page) {
        let sequenceNumber = ++fetchDeliverableXhrCount;
        let filter = {},
            currentComponent = "dashboardMediaCalls";

        filter = JSON.parse(JSON.stringify(getters.dashboardMediaCallsFilter));

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }

        state.currentPage = page;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "widget/getPublicDeliverableList?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );

        if (
            typeof response.data.allDeliverableTemplates != "undefined" &&
            response.data.allDeliverableTemplates.to -
                response.data.allDeliverableTemplates.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }

        if(sequenceNumber == fetchDeliverableXhrCount) {
            fetchDeliverableXhrCount = 0;
            commit(
                "setDeliverableTemplates",
                typeof response.data == "undefined" ? response : response.data
            );
            if(currentComponent != 'projectDeliverable') {
                window.scrollTo(0, 0);
            }
        }
    },
    async fetchDeliverableTemplateNoScroll({ dispatch }, page) {
        const yOffset = window.pageYOffset;
        const result = await dispatch("fetchDeliverableTemplates", page);
        window.scrollTo(0, yOffset);
        return result;
    },

    async fetchWidgetSettings({ commit, getters }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getWidgetSettings",
            {
                params: {
                    currentRoute: router.currentRoute.path
                }
            }
        );
        commit("setWidgetSettings", response.data);
        return response.data;
    },
    async fetchNumberStat({ commit, getters }) {
        let currentComponent = router.currentRoute.name;

        let filter = JSON.parse(
            JSON.stringify(getters.dashboardMediaCallsFilter)
        );
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "widget/getNumberStat?currentComponent=" + currentComponent,
            filter
        );
        commit("setNumberStat", response.data);
        return response.data;
    },
    async fetchDeliverableTemplateCount({ commit, getters }) {
    	return;
        let filter = JSON.parse(
            JSON.stringify(getters.deliverableTemplateFilter)
        );

        if (filter.filterByDate == true) {
            // var s = Vue.moment(filter.dateRange.startDate);
            // s.set('hour', 0);
            // s.set('minute', 0);
            // s.set('second', 0);
            // filter.dateRange.startDate = s.toISOString();
            // var e = Vue.moment(filter.dateRange.endDate);
            // e.set('hour', 23);
            // e.set('minute', 59);
            // e.set('second', 59);
            // filter.dateRange.endDate = e.toISOString();
        }
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }
        if (filter.filterByPublishDate == true) {
            var s = Vue.moment(filter.datePublishRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.datePublishRange.startDate = s.utc().format();
            var e = Vue.moment(filter.datePublishRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.datePublishRange.endDate = e.utc().format();
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "getDeliverableTemplateCount",
            filter
        );
        commit("setDeliverableTemplateCount", response.data);
    },

    async filterDeliverableTemplates({ commit }, filter) {
        commit("clearMergeDeliverableTemplates", null);

        let page = 1;
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getDeliverableTemplates?page=" + page,
            filter
        );
        commit("setDeliverableTemplates", response.data);
        window.scrollTo(0, 0);
    },

    async saveGlobalAlert({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "saveGlobalAlert",
            payload
        );
        commit('setAllAlerts', response.data.allAlerts);
        commit('setUserAlerts', response.data.userAlerts);
    },

    async makeAlertRead({ commit }, payload) {
        let param = {loggedout: 'false'};
        if(typeof payload.alert.loggedout != 'undefined') {
            param = {loggedout: 'true'}
        }
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + 'makeAlertRead/' + payload.alert.id,
            param
        );
        commit('setAllAlerts', response.data.allAlerts);
        commit('setUserAlerts', response.data.userAlerts);
        commit('setNotification', response.data.notification);
    },

    async makeAlertsRead({ commit }, payload) {
        // Mark multiple specific alerts as read by their IDs
        let param = {
            loggedout: 'false',
            alertIds: payload.alertIds,
            markAllFiltered: payload.markAllFiltered || false,
            markGroupAll: payload.markGroupAll || false,
            group_type: payload.group_type || null,
            group_id: payload.group_id || null,
            deliverable_group_id: payload.deliverable_group_id || null,
            project_section_only: payload.project_section_only || false,
            filters: payload.filters || null
        };
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + 'makeAlertsRead',
            param
        );
        commit('setAllAlerts', response.data.allAlerts);
        commit('setUserAlerts', response.data.userAlerts);
        commit('setNotification', response.data.notification);
        return response;
    },

    async makeAllRelatedAlertRead({ commit }, payload) {
        if(payload.groupedModelId !== undefined) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL + 'makeAllRelatedAlertRead/' + payload.groupedModelId,
                payload
            );
            commit('setAllAlerts', response.data.allAlerts);
            commit('setUserAlerts', response.data.userAlerts);
            commit('setNotification', response.data.notification);
        } else {
            console.warn('Cannot make All Related Alert as Read, id is undefined');
        }
    },

    async deleteAlert({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + "deleteAlert/" + payload.alertId
        );
        commit('setAllAlerts', response.data.allAlerts);
        commit('setUserAlerts', response.data.userAlerts);
        commit('setNotification', response.data.notification);
    },

    async fetchNotification({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchNotification/' + payload.userId,
        );
        if(response.data.length) {
            response.data.forEach(function(value, key) {
                if(value.status == 0 && value.alert_type == 'system-update') {
                    toast.permanentWarning(value.message + ' <a href="#" data-alertId="'+value.id+'" id="update-logout">Click here</a> to logout', value.title);
                }
            });
        }
        commit('setNotification', response.data);
    },

    async fetchUserAlerts({ commit }, payload) {
        let params = {};
        
        if (typeof payload === 'object' && payload !== null) {
            // If payload is an object with filter parameters
            params = { ...payload };
            
            // Ensure alert_types is properly formatted as array of slugs
            if (params.alert_types && Array.isArray(params.alert_types)) {
                params.alert_types = params.alert_types.map(type => 
                    typeof type === 'object' ? type.slug : type
                ).filter(slug => slug); // Remove any null/undefined values
            }
            
            // Ensure page is set
            if (!params.page) {
                params.page = 1;
            }
        } else {
            // Legacy behavior - payload is just page number
            params.page = payload || 1;
        }
        
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "fetchUserAlerts",
            params
        );
        commit('setUserAlerts', response.data);
    },

    async fetchAlerts({ commit }, payload) {
        let page = payload;
        if(typeof page == 'undefined') {
            page = 1;
        }
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "fetchAlerts",
            {params: {page: page}}
        );
        commit('setAllAlerts', response.data);
    },

    async fetchUsers({ commit, getters }, payload) {
        if(getters.allUsers.length == 0 || (typeof payload != 'undefined' && payload.forced)) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getUsers"
            );
            commit("setUsers", response.data);
        }
    },

    async fetchActiveUsers({ commit, getters }, payload) {
        if(getters.allUsers.length == 0 || (typeof payload != 'undefined' && payload.forced)) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getActiveUsers"
            );
            commit("setActiveUsers", response.data);
        }
    },

    async fetchUserPermissions() {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchUserPermissions'
        );
        this.state.userAbilities = response.data;
    },

    async fetchViews({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchViews'
        );
        commit('setViews', response.data);
    },

    async saveView({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + 'saveView',
            payload
        );
        let that = this;
        if (router.currentRoute.name == "dashboard") {
            payload.viewId != '' ? localStorage.setItem('dashboardActiveSave', payload.viewId) : localStorage.setItem('dashboardActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "myWeek") {
            payload.viewId != '' ? localStorage.setItem('myWeekActiveSave', payload.viewId) : localStorage.setItem('myWeekActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "mediaCallPage") {
            payload.viewId != '' ? localStorage.setItem('mediaCallActiveSave', payload.viewId) : localStorage.setItem('mediaCallActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "project") {
            payload.viewId != '' ? localStorage.setItem('projectActiveSave', payload.viewId) : localStorage.setItem('projectActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "myProjects") {
            payload.viewId != '' ? localStorage.setItem('myProjectsActiveSave', payload.viewId) : localStorage.setItem('myProjectsActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "userGlobalAlert") {
            payload.viewId != '' ? localStorage.setItem('notificationActiveSave', payload.viewId) : localStorage.setItem('notificationActiveSave', response.data[0].id);
        }
        commit('setViews', response.data);
    },

    async removeView({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + 'removeView/' + payload
        );
        commit('setViews', response.data);
    },

    async fetchSystemSettings({ commit, getters, dispatch }) {
        // Check if system settings is empty (default)
        if(Object.keys(getters.systemSettings).length === 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getSystemSettings"
            );
            commit("setSystemSettings", response.data);
        }
    },



    async fetchAllGroupedDeliverableTemplates({ commit, getters }, payload) {
    	let filter = {};
		filter.socialCampaign = [];
		filter.socialSeries = [];

    	if (payload) {
    		if (payload.social_campaign_id) {
    			filter.socialCampaign.push({"id":payload.social_campaign_id});
    		}
    		if (payload.social_series_id) {
    			filter.socialSeries.push({"id":payload.social_series_id});
    		}
    	}
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableTemplates?page=" +
                1 +
                "&currentComponent=" +
                "socialMedia",
            filter
        );
            commit(
                "mergeDeliverableTemplates",
                typeof response.data == "undefined" ? response : response.data
            );
    },

    async fetchSocialSeries({ commit, getters }) {
        if(getters.allSocialSeries.length == 0) {
            const response = await axios.post(
                process.env.MIX_API_BASE_URL + "getSocialSeries"
            );
            commit("setAllSocialSeries", response.data.allSocialSeries.data);
        }
    },

    async fetchSocialCampaigns({ commit, getters }) {
        if(getters.allSocialCampaigns.length == 0) {
            const response = await axios.post(
                process.env.MIX_API_BASE_URL + "getSocialCampaigns"
            );
            commit("setAllSocialCampaigns", response.data.allSocialCampaigns.data);
        }
    },

    async fetchDepartments({ commit, getters }) {
        if(getters.allDepartments.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getDepartments"
            );
            commit("setDepartments", response.data);
        }
    },

    async fetchActiveDepartments({ commit, getters }) {
        if(getters.allDepartments.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getActiveDepartments"
            );
            commit("setActiveDepartments", response.data);
        }
    },

    async fetchTags({ commit, getters }) {
        if(getters.allTags.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getTags"
            );
            commit("setTags", response.data);
        }
    },

    async fetchSubjects({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getSubjects"
        );
        commit("setSubjects", response.data);
    },

    async fetchContacts({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getContacts"
        );
        commit("setContacts", response.data);
    },

    async fetchActiveContacts({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getActiveContacts"
        );
        commit("setActiveContacts", response.data);
    },

    async getProjectDetail({ commit }, projectId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getProjectDetail/" +
                projectId.projectId
        );
        commit(
            "setProjectDetail",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async getDeliverableDetailOnly({ commit }, deliverableTemplateId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableDetail/" +
                deliverableTemplateId.deliverableTemplateId
        );
        commit(
            "setDeliverableTemplateDetailOnly",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async getDeliverableTemplateDetail({ commit }, deliverableTemplateId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "deliverableTemplates/getDeliverableTemplateDetail/" +
                deliverableTemplateId.deliverableTemplateId
        );
        commit(
            "setDeliverableTemplateDetail",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async downloadSocialPostsFiles({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "downloadSocialPostsFiles/" +
                payload.deliverableTemplateId,
            {
                responseType: "blob"
            }
        );
        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'download.zip';
        if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (fileNameMatch && fileNameMatch.length > 1) {
                fileName = fileNameMatch[1];
            }
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    },

    async getSocialSeriesDetail({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getSocialSeriesDetail/" +
                payload.seriesId
        );
        commit(
            "setSocialSeriesDetail",
            typeof response.data == "undefined" ? response : response.data.data
        );
    },

    async getDeliverableDetailByTask({ commit }, taskId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableDetailByTask/" +
                taskId.taskId
        );
        commit(
            "setDeliverableTemplateDetail",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async sendFeedback({ commit }, feedbackData) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "sendFeedback",
            feedbackData
        );
        return response;
    },

    async createTask({ commit, dispatch }, deliverableTemplateId) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "deliverableTemplates/createTask/media_call/" +
                deliverableTemplateId
        );
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setProjectDeliverable", response.data.deliverableTemplateDetail);
        commit("groupUserDeliverable");

        // if(router.currentRoute.name == 'projectDeliverable') {
        //     let projectId = router.currentRoute.params.projectId
        //     dispatch(
        //         "fetchProjectDeliverable"
        //     )
        // }

        if (typeof response.data.newTaskId != 'undefined') {
            return response.data.newTaskId
        }
    },

    async removeTask({ commit,dispatch }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "deliverableTemplates/removeTask/" +
                payload.deliverableTemplateId +
                "/" +
                payload.dtTaskId
        );
        // if(router.currentRoute.name == 'projectDeliverable') {
        //     dispatch(
        //         "fetchProjectDeliverable"
        //     )
        // }
        return response.data;
    },

    async updateDepartmentFieldValue({ getters, commit, dispatch }, fieldUpdateData) {
        let updateData = {};
        if(typeof fieldUpdateData.field_id != 'undefined') {
            let fieldValueData = fieldUpdateData.field_key != 'color_picker' ? fieldUpdateData.value : fieldUpdateData.value.hex;
            updateData = {
                compontent: router.currentRoute.name,
                id: fieldUpdateData.id,
                parentId: fieldUpdateData.parent_id,
                fieldId: fieldUpdateData.field_id,
                departmentFieldId: fieldUpdateData.departmentFieldId,
                value: fieldValueData,
                departmentId: typeof fieldUpdateData.department_id == 'undefined' ? fieldUpdateData.id : fieldUpdateData.department_id
            };
        } else {
            updateData = {
                compontent: router.currentRoute.name,
                value: fieldUpdateData.title,
                departmentId:
                    typeof fieldUpdateData.departmentId != "undefined"
                        ? fieldUpdateData.departmentId
                        : fieldUpdateData.id,
                departments: fieldUpdateData.departments,
            };
        }
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateDepartmentFieldValue",
            updateData
        );
        // if(router.currentRoute.name == 'department') {
        //     dispatch('fetchDepartmentsNoScroll', getters.currentPage);
        // }
        // if(router.currentRoute.name == 'departmentDeliverable') {
        //     // commit("setDepartment", response.data.departmentDetail);
        //     commit("setDepartmentWithField", response.data);
        // }
        commit("setIsDirty",false);
    },


    async updateProjectDepartments({ getters, commit, dispatch, state }, fieldUpdateData) {
        let updateData = {};
        let x = 0;
        updateData = {
            projectId:
                typeof fieldUpdateData.projectId != "undefined"
                    ? fieldUpdateData.projectId
                    : fieldUpdateData.id,
            departments: fieldUpdateData.departments,
        };
        let existing_project = null;

        if(router.currentRoute.name == 'myProjects') {
        	if (
        	    typeof state.userProjects != "undefined"
        	) {
        	    for (x = 0; x < Object.keys(state.userProjects).length; x++) {
        	        if (typeof state.userProjects[x] != 'undefined' && state.userProjects[x].id == updateData.projectId) {
        	        	existing_project = _.cloneDeep(state.userProjects[x]);
						break;
        	        }
        	    }
        	}
        }
        if(router.currentRoute.name == 'project') {
        	if (
        	    typeof state.projects != "undefined"
        	) {
        	    for (x = 0; x < Object.keys(state.projects).length; x++) {
        	        if (typeof state.projects[x] != 'undefined' && state.projects[x].id == updateData.projectId) {
        	        	existing_project = _.cloneDeep(state.projects[x]);
						break;
        	        }
        	    }
        	}
        }
        if(router.currentRoute.name == 'projectDeliverable') {
			existing_project = _.cloneDeep(state.projectDetail);
        }
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProjectDepartments",
            updateData
        );
        if(router.currentRoute.name == 'myProjects') {
            await dispatch('fetchUserProjectsNoScroll', getters.currentPage)
        }
        if(router.currentRoute.name == 'project') {
            await dispatch('fetchProjectsNoScroll', getters.currentPage);
        }
        if(router.currentRoute.name == 'projectDeliverable') {
            if (state.projectDetail) {
            	if (state.projectDetail.id == response.data.projectDetail.id) {
            		if (state.projectDetail.engagement_manager.id != response.data.projectDetail.engagement_manager.id) {
            			let msg = "Project Engagement Manager has been changed from '" + state.projectDetail.engagement_manager.name + "' to '" + response.data.projectDetail.engagement_manager.name + "' for the chosen department";

            			if (state.projectDetail.engagement_manager_locked  != response.data.projectDetail.engagement_manager_locked) {
            				if (!response.data.projectDetail.engagement_manager_locked) {
            					msg += " and is UNLOCKED";
            				}
            			}
            			msg += ".";
            			commit("setProject", response.data.projectDetail);
						toast.infoTop(msg);
            		}
            	}
            }
        }
        if(router.currentRoute.name == 'myProjects') {
        	if (
        	    typeof state.userProjects != "undefined"
        	) {
        	    for (x = 0; x < Object.keys(state.userProjects).length; x++) {

        	        if (typeof state.userProjects[x] != 'undefined' && state.userProjects[x].id == updateData.projectId) {
	            		if (state.userProjects[x].engagement_manager.id != existing_project.engagement_manager.id) {
    	            			let msg = "Project Engagement Manager has been changed from '" + existing_project.engagement_manager.name + "' to '" + state.userProjects[x].engagement_manager.name + "' for the chosen department";

	            			if (state.userProjects[x].engagement_manager_locked  != existing_project.engagement_manager_locked && existing_project.engagement_manager_locked != null) {
	            				if (!state.userProjects[x].engagement_manager_locked) {
	            					msg += " and is UNLOCKED";
	            				}
	            			}
	            			msg += ".";
							toast.infoTop(msg);
							break;
	            		}
        	        }
        	    }
        	}
        }

        if(router.currentRoute.name == 'project') {
        	if (
        	    typeof state.projects != "undefined"
        	) {
        	    for (x = 0; x < Object.keys(state.projects).length; x++) {

        	        if (typeof state.projects[x] != 'undefined' && state.projects[x].id == updateData.projectId) {
	            		if (state.projects[x].engagement_manager.id != existing_project.engagement_manager.id) {
    	            			let msg = "Project Engagement Manager has been changed from '" + existing_project.engagement_manager.name + "' to '" + state.projects[x].engagement_manager.name + "' for the chosen department";

	            			if (state.projects[x].engagement_manager_locked  != existing_project.engagement_manager_locked && existing_project.engagement_manager_locked != null) {
	            				if (!state.projects[x].engagement_manager_locked) {
	            					msg += " and is UNLOCKED";
	            				}
	            			}
	            			msg += ".";
							toast.infoTop(msg);
							break;
	            		}
        	        }
        	    }
        	}
        }


        commit("setIsDirty",false);
    },


    async updateProjectFieldValue({ getters, commit, dispatch }, fieldUpdateData) {
        let updateData = {};
        if(typeof fieldUpdateData.field_id != 'undefined') {
            let fieldValueData = fieldUpdateData.field_key != 'color_picker' ? fieldUpdateData.value : fieldUpdateData.value.hex;
            updateData = {
                compontent: router.currentRoute.name,
                id: fieldUpdateData.id,
                parentId: fieldUpdateData.parent_id,
                fieldId: fieldUpdateData.field_id,
                projectFieldId: fieldUpdateData.projectFieldId,
                value: fieldValueData,
                projectId: typeof fieldUpdateData.project_id == 'undefined' ? fieldUpdateData.id : fieldUpdateData.project_id
            };
        } else {
            updateData = {
                compontent: router.currentRoute.name,
                value: fieldUpdateData.title,
                projectId:
                    typeof fieldUpdateData.projectId != "undefined"
                        ? fieldUpdateData.projectId
                        : fieldUpdateData.id,
                departments: fieldUpdateData.departments,
            };
        }
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProjectFieldValue",
            updateData
        );
        if(
            response.data.updatedField == "title" &&
            response.data.projectDetail.id === getters.breadcrumbs.parent_project_id
        ) {
            // set new title to breadcrumbs if project_id same
            commit(
                "setBreadcrumbs",
                {
                    ...getters.breadcrumbs,
                    "parent_project_title": response.data.projectDetail.title,
                }
            );
        }
        if(
            response.data.updatedField == "project_status"
        ) {
            var newStatus = null;
            for (var x = 0; x < response.data.projectDetail.customFields.length ;x++) {
            	if (response.data.projectDetail.customFields[x].field_key == response.data.updatedField) {
            		newStatus = JSON.parse(response.data.projectDetail.customFields[x].value);
            	}
            }
            commit(
                "setProjectStatusForDeliverableTemplates",
                {
                	"project_id":response.data.projectDetail.projectId,
                	"value":newStatus
                }
            );


        }
        if(router.currentRoute.name == 'myProjects') {
            dispatch('fetchUserProjectsNoScroll', getters.currentPage)
        }
        if(router.currentRoute.name == 'project') {
            dispatch('fetchProjectsNoScroll', getters.currentPage);
        }
        if(router.currentRoute.name == 'projectDeliverable') {
            // commit("setProject", response.data.projectDetail);
            commit("setProjectWithField", response.data);
        }
        commit("setIsDirty",false);
    },


    async toggleProjectEngagementManagerLock({ getters, commit, dispatch }, fieldUpdateData) {
        let updateData = {};

        updateData = {
            projectId:
                typeof fieldUpdateData.projectId != "undefined"
                    ? fieldUpdateData.projectId
                    : fieldUpdateData.id,
        };
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "toggleProjectEngagementManagerLock",
            updateData
        );
        commit("setProjectEngagementManagerLock", response.data);
        // if(router.currentRoute.name == 'myProjects') {
        //     dispatch('fetchUserProjectsNoScroll', getters.currentPage)
        // }
        // if(router.currentRoute.name == 'project') {
        //     dispatch('fetchProjectsNoScroll', getters.currentPage);
        // }
        if(router.currentRoute.name == 'projectDeliverable') {
            commit("setProjectWithField", response.data);
        }
        commit("setIsDirty",false);
    },



    async setDeliverableProject({ commit, dispatch }, data) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "setDeliverableProject/",
            data
        );
        if(router.currentRoute.name == 'projectDeliverable') {
            dispatch('fetchProjectDeliverable');
        }
        return response.data;
    },

    async addDeliverableToProject({ commit }, data) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableToProject/",
            data
        );
        commit(
            "setProjectDeliverableTemplates",
            response.data
        );
        commit(
            "setProjectDetail",
            response.data.projectDetail
        );
        return response.data;
    },

    async updateMultipleUsersFieldValue({ commit, state, dispatch}, fieldUpdateData) {
        dispatch("updateDeliverableTemplateFieldValue", {
            parentId: fieldUpdateData.parent_id,
            fieldId: fieldUpdateData.id,
            fieldKey: fieldUpdateData.field_key,
            value: fieldUpdateData.value,
            deliverableTemplateId: fieldUpdateData.deliverableTemplateId
        }).then(function(resp) {
            if(router.currentRoute.name == 'projectDeliverable') {
                dispatch(
                    "fetchProjectDeliverable"
                )
            }
        });
    },

    async updateFieldValue({ commit, state, dispatch}, fieldUpdateData) {
        if (fieldUpdateData.field_type == "datepicker") {
            // convert the value to utc?
            if (fieldUpdateData.value != null) {
                let date = new Date();
                let currentTime =
                    date.getUTCHours() +
                    ":" +
                    date.getUTCMinutes() +
                    ":" +
                    date.getUTCSeconds();
                fieldUpdateData.value =
                    Vue.moment(fieldUpdateData.value).format("YYYY-MM-DD") +
                    " " +
                    currentTime;
            }

            // convert the value to UTC?
            // fieldUpdateData.value = Vue.moment(fieldUpdateData.value).utc();
        }
        if (
            typeof fieldUpdateData.taskFields !== "undefined" ||
            typeof fieldUpdateData.taskFieldId !== "undefined"
        ) {
            let taskFieldId = fieldUpdateData.taskFieldId,
                fieldValue = fieldUpdateData.title
                    ? fieldUpdateData.title
                    : fieldUpdateData.value,
                taskId =
                    typeof fieldUpdateData.id == "undefined"
                        ? fieldUpdateData.taskId
                        : fieldUpdateData.id;
            return await dispatch("updateTaskFieldValue", {
                taskFieldId,
                fieldValue,
                taskId
            }).then(function(resp) {
                	if(router.currentRoute.name == 'projectDeliverable') {
                	    dispatch(
                	        "fetchProjectDeliverable"
                	    )
                	}
                    return Promise.resolve(resp);
                }).catch(function(error) {
                    return Promise.reject(error);
                });
        } else {
            if (typeof fieldUpdateData.field_id !== "undefined") {
                return dispatch("updateDeliverableTemplateFieldValue", {
                    id: fieldUpdateData.id,
                    parentId: fieldUpdateData.parent_id,
                    fieldId: fieldUpdateData.field_id,
                    fieldKey: fieldUpdateData.field_key,
                    value: fieldUpdateData.value,
                    deliverableTemplateId: fieldUpdateData.deliverableTemplate_id
                }).then(function(resp) {
                	if(router.currentRoute.name == 'projectDeliverable') {
                	    dispatch(
                	        "fetchProjectDeliverable"
                	    )
                	}
                    return resp;
                });
            } else {
                return dispatch("updateDeliverableTemplateFieldValue", {
                    value: fieldUpdateData.title,
                    deliverableTemplateId:
                        typeof fieldUpdateData.deliverableTemplateId != "undefined"
                            ? fieldUpdateData.deliverableTemplateId
                            : fieldUpdateData.id,
                    departments: fieldUpdateData.departments,
                    subjects: fieldUpdateData.subjects,
                    tags: fieldUpdateData.tags
                }).then(function(resp) {
                	if(router.currentRoute.name == 'projectDeliverable') {
                	    dispatch(
                	        "fetchProjectDeliverable"
                	    )
                	}
                    if(
                        resp.data.updatedField == "title" &&
                        resp.data.deliverableTemplateDetail.id === getters.breadcrumbs.parent_deliverableTemplate_id
                    ) {
                        // set new title to breadcrumbs if deliverableTemplate_id same
                        commit(
                            "setBreadcrumbs",
                            {
                                ...getters.breadcrumbs,
                                "parent_deliverableTemplate_title": resp.data.deliverableTemplateDetail.title,
                            }
                        );
                    }
                    return resp;
                });
            }
        }

    },

    async updateTaskFieldValue({ commit, state, dispatch}, fieldUpdateData) {
      try {
        let response = await axios.put(
            process.env.MIX_API_BASE_URL + "deliverableTemplates/updateTaskFieldValue",
            fieldUpdateData
        );

        if(response.data.taskFieldDetail != 'status_note') {
            commit("setDeliverable", response.data.deliverableTemplateDetail);
            commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        }

        let refreshListQuery = ["title", "status_note"];

        if (typeof response.data.taskFieldDetail != "undefined") {
            if (
                (state.deliverableTemplateFilter.query != "" &&
                    refreshListQuery.indexOf(
                        response.data.taskFieldDetail
                    ) != -1) ||
                (state.deliverableTemplateFilter.status.length >
                    0 &&
                    response.data.taskFieldDetail == "status") ||
                (state.deliverableTemplateFilter.coordinator
                    .length > 0 &&
                    response.data.taskFieldDetail == "user") ||
                (state.deliverableTemplateFilter.filterByDate &&
                    response.data.taskFieldDetail == "due_date")
            ) {
                dispatch(
                    "refreshWithMatchingTaskFilters",
                    response.data.taskFieldDetail
                );
            }
        }
        if(response.data.taskFieldDetail == 'user') {
            commit("changeForceUpdateCounter");
        }
        if(response.data.taskFieldDetail == 'due_date') {
            dispatch("fetchDeliverableTemplateCount");
        }

        return Promise.resolve(response);

      } catch (error) {
          console.error(error);
          return Promise.reject(error);
      }
    },

    async refreshWithMatchingTaskFilters({ commit, getters, dispatch }, taskFieldDetail) {
        dispatch("fetchDeliverableTemplateNoScroll", getters.currentPage);
    },

    async getPreference({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getPreference"
        );
        commit("setUserPreference", response.data.preferenceDetail);
        commit("setLoggedInUserData", response.data.loggedInUserData);
    },

    async savePreference({ commit }, preferenceData) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "savePreference",
            {
                preferenceData: preferenceData
            }
        );

        if (router.currentRoute.name == "myWeek") {
            commit("changeForceUpdateCounter");
        }
        commit("setUserPreference", response.data.preferenceDetail);
    },

    async updateDeliverableTemplateFieldValue({ commit, getters, dispatch  }, fieldUpdateData) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "deliverableTemplates/updateDeliverableTemplateFieldValue",
            fieldUpdateData
        );
        // commit setDeliverableTemplateDetail is commented because update field calls this api again 
        //commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        return response;
    },



    async updateDeliverableTemplateTitle({ commit, getters, dispatch  }, newTitle) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "deliverableTemplates/updateDeliverableTemplateTitle",
            {
                id: newTitle.id,
                title: newTitle.title
            }
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        
        // dispatch("getDeliverableTemplates");
        // // if (router.currentRoute.name == "myWeek" || router.currentRoute.name == "dashboard" || router.currentRoute.name == "mediaCallPage") {
        //     commit("setDataLoading", true);
        //     dispatch("fetchDeliverableTemplates").then(() => {
        //         commit("setDataLoading", false);
        //     });
        // // }
        // if (router.currentRoute.name == "projectDeliverable") {
        //     commit("setDataLoading", true);
        //     dispatch("fetchProjectDeliverable").then(() => {
        //         commit("setDataLoading", false);
        //     });
        // }

        // dispatch("getPreference");
        // dispatch("getPreferenceDeliverableTypes");
        return response;
    },


    async updateDeliverableTemplateDefaultTitle({ commit, getters, dispatch  }, newTitle) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "deliverableTemplates/updateDeliverableTemplateDefaultTitle",
            {
                id: newTitle.id,
                default_title: newTitle.default_title
            }
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        return response;
    },

    async addDeliverableContact({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableContact/" +
                payload.deliverableTemplateId,
            payload.contactData
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        if (response.data.contactAdded) {
            // dispatch('countOutlets')
            dispatch("fetchDeliverableTemplates");
        }
    },

    async countOutlets({ commit, getters }) {
        let filter = false;
        // if (router.currentRoute.name == "myWeek") {
        //     filter = JSON.parse(
        //         JSON.stringify(getters.myWeekDeliverableFilter)
        //     );
        // } else if (router.currentRoute.name == "mediaCallPage") {
        //     filter = JSON.parse(JSON.stringify(getters.mediaCallFilter));
        // } else {
            filter = JSON.parse(JSON.stringify(getters.deliverableTemplateFilter));
        // }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "countOutlets",
            filter
        );
        commit("setOutletCount", response.data);
    },

    async changeLeadCoordinator({ commit }, payload) {
        if(payload.lead_coordinator != null) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL +
                    "changeLeadCoordinator/" +
                    payload.id,
                payload.lead_coordinator
            );
            // commit("setDeliverableTemplates", response.data);
            commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
            commit("setDeliverable", response.data.deliverableTemplateDetail);
        }
    },

    async changeProjectEngagementManager({ getters, commit, dispatch }, payload) {
        if (payload.engagement_manager != null) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL +
                    "changeProjectEngagementManager/" +
                    payload.id,
                payload.engagement_manager
            );
            commit("setProjectDetail", response.data);
            if (router.currentRoute.name == 'myProjects') {
                dispatch('fetchUserProjectNoSet', getters.currentPage).then((r) => {
                    const list = r.data.userProjects.data;
                    for (let i in list) {
                        if (list[i].id == response.data.data.id) {
                            return;
                        }
                    }

                    // const orgProject = getters.userProjects.find(x => x.id == response.data.data.id);
                    // orgProject.notAvailable = true;
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-no-relation-area`).addClass("show");
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-content`).addClass("hide");
                    dispatch("manageProjectUndoActivation", response.data.data.id);
                });
            }
        }
    },
    async changeProjectLead({ getters, commit, dispatch }, payload) {
        if (payload.project_lead != null) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL +
                    "changeProjectLead/" +
                    payload.id,
                payload.project_lead
            );
            commit("setProjectDetail", response.data);
            if (router.currentRoute.name == 'myProjects') {
                dispatch('fetchUserProjectNoSet', getters.currentPage).then((r) => {
                    const list = r.data.userProjects.data;
                    for (let i in list) {
                        if (list[i].id == response.data.data.id) {
                            return;
                        }
                    }

                    // const orgProject = getters.userProjects.find(x => x.id == response.data.data.id);
                    // orgProject.notAvailable = true;
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-no-relation-area`).addClass("show");
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-content`).addClass("hide");
                    dispatch("manageProjectUndoActivation", response.data.data.id);
                });
            }
        }
    },

    async createRole({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + 'createRole',
            {newRole: payload.newRole}
        );
        commit('setAllRoles', response.data.allRoles);
        return response.data.newRole;
    },

    async updateRole({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + 'updateRole/' + payload.id,
            payload
        );
        if(response.data == false) {
            toast.error('Role name must be unique.');
        } else {
            commit('setAllRoles', response.data);
        }
        return response.data;
    },

    async removeRole({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + 'removeRole/' + payload.id
        );
        if(response.data == false) {
            toast.error('Role is in use and cannot be removed at the moment.');
        } else {
            commit('setAllRoles', response.data);
        }
        return response.data;
    },
    async getDeliverableTemplateRoles({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'deliverableTemplates/getDeliverableTemplateRoles/' + payload
        );
        // if(response.data == false) {
        //     toast.error('Unable to get Deliverable Template Roles');
        // } else {
            commit('setDeliverableTemplateRoles', response.data);
        // }
        return response.data;
    },

    async createDeliverableType({ commit }, deliverableTemplate) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createDeliverableType",
            deliverableTemplate
        );
        commit("setDeliverableTypes", response.data);
    },

    async createTag({ commit }, newTag) {
        let deliverableTemplateId =
            typeof newTag.deliverableTemplateId == "undefined"
                ? this.getters.deliverableTemplateDetail.deliverableTemplateId
                : newTag.deliverableTemplateId;
        if (deliverableTemplateId == null) {
            if (this.getters.deliverableTemplateDetail.deliverableTemplateId > 0) {
                deliverableTemplateId = this.getters.deliverableTemplateDetail.deliverableTemplateId;
            }
        }

        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createTag/" + deliverableTemplateId,
            {
                newTag:
                    typeof newTag.newTag != "undefined" ? newTag.newTag : newTag
            }
        );
        commit("setTags", response.data.tags);
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        commit("groupUserDeliverable");
    },

    async createCustomDeliverableTag({ commit }, payload) {
        let deliverableTemplateId =
            typeof payload.deliverableTemplateId == "undefined"
                ? this.getters.deliverableTemplateDetail.deliverableTemplateId
                : payload.deliverableTemplateId;
        if (deliverableTemplateId == null) {
            if (this.getters.deliverableTemplateDetail.deliverableTemplateId > 0) {
                deliverableTemplateId = this.getters.deliverableTemplateDetail.deliverableTemplateId;
            }
        }

        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createCustomDeliverableTag/" + deliverableTemplateId,
            {
                newTag:
                    typeof payload.newTag != "undefined" ? payload.newTag : payload
            }
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async removeDeliverableContact({ commit }, contactId) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableContact/" +
                this.getters.deliverableTemplateDetail.deliverableTemplateId +
                "/contactId/" +
                contactId
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async addDeliverableSpokesperson({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableSpokesperson/" +
                payload.deliverableTemplateId,
            payload.contactData
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },
    async removeDeliverableSpokesperson({ commit }, contactId) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableSpokesperson/" +
                this.getters.deliverableTemplateDetail.deliverableTemplateId +
                "/contactId/" +
                contactId
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async addDeliverableWho({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableWho/" +
                payload.deliverableTemplateId,
            payload.contactData
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        if (response.data.contactAdded) {
            dispatch("countOutlets");
        }
    },
    async removeDeliverableWho({ commit, dispatch, state }, contactId) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableWho/" +
                state.deliverableTemplateDetail.deliverableTemplateId +
                "/contactId/" +
                contactId
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        dispatch("countOutlets");
    },

    
    async addDeliverableCfContact({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableCfContact/" +
                payload.deliverableTemplateId,
            {...payload},
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },
    async removeDeliverableCfContact({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableCfContact/" +
                payload.belongsToId +
                "/contactId/" +
                payload.contactId,
                {
                    params: {
                        currentFieldKey: payload.currentFieldKey
                    }
                }
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async createProjectCfContact({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createProjectCfContact",
            {
                component: router.currentRoute.name,
                ...payload
            }
        );
        commit("setProjectDetail", response.data.projectDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateProjectCfContact({ commit, state }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProjectCfContact",
            payload
        );
        commit("setProjectDetail", response.data.projectDetail);
        commit("setContacts", response.data.contacts);
    },

    async addProjectContact({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addProjectContact/" +
                payload.projectId,
                {
                    component: router.currentRoute.name,
                    ...payload.contactData,
                    ...payload
                }
        );
        commit("setProjectDetail", response.data.projectDetail);
        if (response.data.contactAdded) {
            dispatch("fetchDeliverableTemplates");
        }
    },

    async removeProjectCfContact({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeProjectCfContact/" +
                payload.belongsToId +
                "/contactId/" +
                payload.contactId ,
                {
                    params: {
                        currentFieldKey: payload.currentFieldKey
                    }
                }
        );
        commit("setProjectDetail", response.data.projectDetail);
    },

    async createNewProject({ commit }, payload) {
        toast.info('Creating project...');
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
            'createNewProject'
        );
        toast.hide();
        commit("setProjectDetail", response.data.projectDetail);
        return response.data.projectDetail;
    },

    async createNewDeliverableTemplateFromId({ commit, dispatch }, payload) {
        toast.info("Creating deliverableTemplate...");
        console.log(payload);
        var more_fields = {};
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "deliverableTemplates/createNewDeliverableTemplateFromId/" +
                payload,
                more_fields
        );
        toast.hide();
        // commit("setDeliverableTemplates", response.data);
        dispatch("getDeliverableTemplates");
        if (response.data.deliverableTemplateDetail) {
	        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        }
        return response;
    },

    async createDeliverableForProject({ commit }, payload) {
        toast.info("Creating deliverableTemplate...");
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "createDeliverableForProject/" +
                payload.projectId + "/" + payload.type
        );
        toast.hide();
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
    },

    async removeDeliverableTemplate({ commit, state, dispatch }, payload) {
        toast.info("Removing deliverableTemplate...");
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "deliverableTemplates/removeDeliverableTemplate/" +
                payload.id
        ).then(response => {
        		let x = 0;
			    if (
			        typeof state.deliverableTemplates != "undefined"
			    ) {
			        for (x = 0; x < Object.keys(state.deliverableTemplates).length; x++) {
			            if (typeof state.deliverableTemplates[x] != 'undefined' && state.deliverableTemplates[x].id == payload.id) {
			                Vue.delete(state.deliverableTemplates, x);
			            }
			        }
			    }

                if (
                    typeof state.allDeliverableTemplates != "undefined"
                ) {
                    for (x = 0; x < Object.keys(state.allDeliverableTemplates).length; x++) {
                        if (typeof state.allDeliverableTemplates[x] != 'undefined' && state.allDeliverableTemplates[x].id == payload.id) {
                            Vue.delete(state.allDeliverableTemplates, x);
                        }
                    }
                }

                return {...response};
  			})
		.catch(error => {
            console.error(error);
            if (error.response && error.response.status) {
                return {status:error.response.status};
            }
    	});
        return response;
    },

    async removeProjectDeliverable({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
            'removeProjectDeliverable',
            payload
        );
        commit("setProjectDetail", response.data.projectDetail);
    },

    async removeProject({ commit, dispatch }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeProject/" +
                payload.projectId
        );
        commit("setProjects", response.data);
        // dispatch("fetchDeliverableTemplates");
        //commit("setDeliverableTemplates", response.data);
        //commit("toggleEditDeliverableModal");
    },


    async removeSocialCampaign({ commit, dispatch }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeSocialCampaign/" +
                payload.socialCampaignId
        );
        commit("setAllSocialCampaigns", response.data.allSocialCampaigns.data);


		if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
    },

    async removeSocialSeries({ commit, dispatch }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeSocialSeries/" +
                payload.socialSeriesId
        );
        commit("setAllSocialCampaigns", response.data);

		if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
    },



    async removeProjectLinkUrl({ commit, state}, payload) {
        payload.projectId = state.projectDetail.projectId;
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + "removeProjectLinkUrl/"
                + payload.projectId + "/" + payload.linkUrl
            ,
        );
        commit("setProjectDetail", response.data.projectDetail);
        commit("setProject", response.data.projectDetail);
    },

    async duplicateMediaCall({ commit }, payload) {

        // toast.info('Copying media call...');
        toast.info('Copying deliverableTemplate...');
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
            'duplicateMediaCall/' + payload.deliverableTemplateId
        );
        if(response.data.deliverableTemplateDetail) {
        	commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        	commit("setDeliverable", response.data.deliverableTemplateDetail);
        }
        toast.hide();
    },

    async duplicateMediaCallFromProject({ commit }, payload) {
        // toast.info('Copying media call...');
        toast.info('Copying deliverableTemplate...');
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
            'duplicateMediaCallFromProject/' + payload.deliverableTemplateId
        );
        toast.hide();
    },

    async duplicateSocialMedia({ commit, state }, payload) {
        toast.info('Copying social media...');
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
            'duplicateSocialMedia/' + payload.deliverableTemplateId
        );
        if(response.data.deliverableTemplateDetail) {
        	commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        	commit("setDeliverable", response.data.deliverableTemplateDetail);
        }
        toast.hide();
    },

    async createContact({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addContact",
            payload
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        commit("setContacts", response.data.contacts);
    },

    async createSpokesperson({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addSpokesperson",
            payload
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        commit("setContacts", response.data.contacts);
    },
    async createWho({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addWho",
            payload
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        commit("setContacts", response.data.contacts);
        commit("setActiveContacts", response.data.activeContacts);
        commit("increaseOutletCount");
    },

    async fetchGeoData({ commit, getters }) {
        if(getters.allCountries.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "fetchGeoData"
            );
            commit("setGeoData", response.data);
        }
    },

    async fetchAllRoles({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "fetchAllRoles"
        );
        commit('setAllRoles', response.data);
    },

    async fetchAllAbilities({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchAllAbilities'
        );
        commit('setAllAbilities', response.data);
    },

    async fetchRolePermissions({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchRolePermissions/' + payload.roleId
        );
        return response.data;
    },
    async fetchRolePreference({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchRolePreference/' + payload.roleId
        );
        return response.data;
    },

    async saveRolePermissions({}, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + 'saveRolePermissions/' + payload.roleId,
            {
                ability: payload.ability,
                preference: payload.preference,
            }
        );
    },

    async updateRoles({ commit, state }, payload) {
       payload.deliverableTemplateId = state.deliverableTemplateDetail.id;
       console.log(payload);
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "deliverableTemplates/updateDeliverableTemplateRoles",
            payload
        );
    },



    async updateContact({ commit, state }, payload) {
        payload.deliverableTemplateId = state.deliverableTemplateDetail.deliverableTemplateId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateContact",
            payload
        );
        // commit('setDeliverableTemplates', response.data)
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateDepartment({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateDepartment",
            payload
        );
        commit('updateDepartmentList', response.data)
    },

    async updateTag({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateTag",
            payload
        );
    },

    async updateContactGS({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateContactGS",
            payload
        );
    },

    async updateUser({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateUser",
            payload
        );
        dispatch('fetchActiveUsers', {forced: true});
        dispatch('fetchUsers', {forced: true});
        return response;
    },

    async sendAdminResetPasswordLinkEmail({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "sendAdminResetPasswordLinkEmail",
            payload
        )
        return response;
    },

    async updateProfile({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProfile",
            payload
        );
        // dispatch("getPreferenceDeliverableTypes");
        dispatch("getDeliverableTemplates");
        if (router.currentRoute.name == "myWeek" || router.currentRoute.name == "dashboard" || router.currentRoute.name == "mediaCallPage") {
            commit("setDataLoading", true);
            dispatch("fetchDeliverableTemplates").then(() => {
                commit("setDataLoading", false);
            });
        }
        if (router.currentRoute.name == "projectDeliverable") {
            commit("setDataLoading", true);
            dispatch("fetchProjectDeliverable").then(() => {
                commit("setDataLoading", false);
            });
        }
        return response;
    },

    async updateSpokesperson({ commit, state }, payload) {
        payload.deliverableTemplateId = state.deliverableTemplateDetail.deliverableTemplateId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSpokesperson",
            payload
        );
        // commit('setDeliverableTemplates', response.data)
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateWho({ commit, state }, payload) {
        payload.deliverableTemplateId = state.deliverableTemplateDetail.deliverableTemplateId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateWho",
            payload
        );
        // commit('setDeliverableTemplates', response.data)
        if(response.data.deliverableTemplateDetail) {
            commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
            commit("setDeliverable", response.data.deliverableTemplateDetail);
        }
        //commit("setContacts", response.data.contacts);
    },

    async createCfContact({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addCfContact",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateCfContact({ commit, state }, payload) {
        payload.deliverableTemplateId = state.deliverableTemplateDetail.deliverableTemplateId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateCfContact",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateDeliverableLinkUrl({ commit, state }, payload) {
        payload.deliverableTemplateId = state.deliverableTemplateDetail.deliverableTemplateId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateLinkUrl",
            payload
        );
        // commit('setDeliverableTemplates', response.data)
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },
    async updateProjectLinkUrl({ commit, state }, payload) {
        payload.projectId = state.projectDetail.projectId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProjectLinkUrl",
            payload
        );
        // commit('setDeliverableTemplates', response.data)
        commit("setProjectDetail", response.data.projectDetail);
        commit("setProject", response.data.projectDetail);
    },
    async createDeliverableLinkUrl({ commit, state }, payload) {
        payload.deliverableTemplateId = state.deliverableTemplateDetail.deliverableTemplateId;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addLinkUrl",
            payload
        );
        // commit('setDeliverableTemplates', response.data)
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },
    async createProjectLinkUrl({ commit, state}, payload) {
        payload.projectId = state.projectDetail.projectId;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addProjectLinkUrl",
            payload
        );
        // commit('setDeliverableTemplates', response.data)
        commit("setProjectDetail", response.data.projectDetail);
        commit("setProject", response.data.projectDetail);
    },
    async removeDeliverableLinkUrl({ commit }, value_id) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableFieldValue/" +
                this.getters.deliverableTemplateDetail.deliverableTemplateId +
                "/fieldValueId/" +
                value_id
        );
        // commit("setDeliverableTemplates", response.data);
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async fetchAllCustomFields({ commit, getters }) {
        if(getters.allCustomFields.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "fetchAllCustomFields"
            );
            commit("setCustomFields", response.data);
        }
    },

    async globalSearch({ commit, getters }, payload) {
        let urlQuery = '';
        let globalSearchFilter = getters.globalSearchFilter;
        let activeTab = 'all';
        if(typeof payload != 'undefined' && payload != '') {
            if(payload.type != 'all') {
                activeTab = payload.type;
                urlQuery = '?page='+payload.page+'&type='+payload.type;
                if(typeof payload.query != 'undefined') {
                    globalSearchFilter.query = payload.query;
                }
            }
        }

        if (globalSearchFilter.filterByDateCreated == true) {
            var s = Vue.moment(globalSearchFilter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            globalSearchFilter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(globalSearchFilter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            globalSearchFilter.dateCreatedRange.endDate = e.utc().format();
        }
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "globalSearch" + urlQuery,
            { params: globalSearchFilter }
        );

        commit('setGlobalSearchData', response.data);
    },

    async getReport({ getters }, payload) {
        let filter = JSON.parse(JSON.stringify(getters.reportFilter));

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        let reportType = payload.reportType;
        let page = '';
        if(typeof payload.page != 'undefined') {
            page = '?page='+payload.page;
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "getReport/" + reportType + page,
            filter
        );
        return response.data;
    },

    async downloadReport({getters}, payload) {
        let filter = JSON.parse(JSON.stringify(getters.reportFilter));

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        let reportType = payload.reportType;
        if(typeof payload.page != 'undefined') {
            filter.page = payload.page;
        }
        const response = await axios({
            method: 'post',
            url: process.env.MIX_API_BASE_URL + "getReport/" + reportType + "/" + payload.exportType,
            responseType: 'arrayBuffer',
            data: filter
          });
        return response.data;
    },

    async highlightRelatedTasks({ commit, state }, groupedModelId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchTaskIdsToHighlight/' + groupedModelId,
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
            commit("setTasksToHighlight", response.data);
        } else {
            console.log('No task alerts found.');
        }        
        
        if (state.taskHighlightTimeout) {
            clearTimeout(state.taskHighlightTimeout)
        }

        state.taskHighlightTimeout = setTimeout(() => {
            commit("setTasksToHighlight", []);
        }, 7 * 1000);
    },

    highlightTask({ commit, state }, taskId) {
        commit("setTaskHighlighted", taskId);

        if (state.taskHighlightTimeout) {
            clearTimeout(state.taskHighlightTimeout)
        }

        state.taskHighlightTimeout = setTimeout(() => {
            commit("setTaskHighlighted", null);
        }, 7 * 1000);
    },
    clearTaskHightlighted({ commit, state }) {
        if (state.taskHighlightTimeout) {
            clearTimeout(state.taskHighlightTimeout)
        }
        commit("setTaskHighlighted", null);
    },

    async changeDeliverableType({ commit, state, dispatch }, payload) {
        if(payload.id != null && payload.deliverableTemplate_type != null) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL +
                    "changeDeliverableType/" +
                    payload.id,
                    {deliverableTemplate_type: payload.deliverableTemplate_type}
            );
            // commit("setDeliverableTemplates", response.data);
            if(response.data.deliverableTemplateDetail) {
            commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
            commit("setDeliverable", response.data.deliverableTemplateDetail);
            }
            if (router.currentRoute.name == "myWeek") {
                commit("groupUserDeliverable");
            }
            if(router.currentRoute.name == 'projectDeliverable') {
                dispatch("fetchProjectDeliverable");
            }
        }
    },

    async updateDeliverableTitle({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateDeliverableTitle/" + payload.id,
            payload
        )
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail)
    },

    async updateDeliverableDescription({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateDeliverableDescription/" + payload.id,
            payload
        )
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
    },
    
    async addDeliverableLanguage({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "addDeliverableLanguage",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
    },

    async removeDeliverableLanguage({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "removeDeliverableLanguage",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
    },

    async addDeliverableSocialNetwork({commit, dispatch}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "addDeliverableSocialNetwork",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);

        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
    },

    async removeDeliverableSocialNetwork({commit, dispatch}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "removeDeliverableSocialNetwork",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);

        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
    },

    async updateSocialMediaPostCaption({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialMediaPostCaption",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async updateSocialMediaPostUrl({commit}, payload){
    	try {
	        const response = await axios.put(
	            process.env.MIX_API_BASE_URL + "updateSocialMediaPostUrl",
	            payload
	        );
	        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
            commit("setDeliverable", response.data.deliverableTemplateDetail);
	    } catch(e) {

	    }

    },

    async getSocialMediaPostShorterUrl({commit}, payload){
    	try {
	        const response = await axios.post(
	            process.env.MIX_API_BASE_URL + "getSocialMediaPostShorterUrl",
	            payload
	        );
            commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
            commit("setDeliverable", response.data.deliverableTemplateDetail);
	    } catch(e) {
	    }
    },
    async getSocialMediaPostLongerUrl({commit}, payload){
    	try {
	        const response = await axios.post(
	            process.env.MIX_API_BASE_URL + "getSocialMediaPostLongerUrl",
	            payload
	        );
            commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
            commit("setDeliverable", response.data.deliverableTemplateDetail);
	    } catch(e) {
	    }
    },

    async updateSocialMediaPostShortUrl({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialMediaPostShortUrl",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);

    },

    async updateSocialPostNetworkShareUrl({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialPostNetworkShareUrl",
            payload
        );

        // commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverableTemplateDetailSocialPostNetworkShareUrl", {payload:payload, response:response.data.deliverableTemplateDetail});

    },

    async createSocialPostComment({commit}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialPostComment",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async removeSocialPostComment({commit}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "removeSocialPostComment",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async getSocialPostImage({commit}, payload){
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "viewFileSocialMediaPost/" + payload.post_id + "/" + payload.file_id + "/" + payload.token
        );
        return response.data;
        //$socialPostId, $fileId, $token)
    },

    async updateFileAltTextSocialMediaPost({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateFileAltTextSocialMediaPost",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
    },

    async updateOrderFileSocialMediaPost({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "reorderFileSocialMediaPost",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async removeFileSocialMediaPost({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "removeFileSocialMediaPost",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async splitSocialMediaNetworkPost({commit}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "splitSocialMediaPostNetwork",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async returnSocialMediaPostNetwork({commit}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "returnSocialMediaPostNetwork",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async updateDeliverablePublishDate({commit, dispatch, getters}, payload){


    	let myRouteName = await dispatch("getRouterNameFromLocationHref");


        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "updateDeliverablePublishDate",
            payload
        );

        // only update the deliverableTemplate detail if changing the one we are editing.
        if (payload.id == getters.deliverableTemplateDetail.id || payload.return_id == getters.deliverableTemplateDetail.id ) {
        	commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
	        commit("setDeliverable", response.data.deliverableTemplateDetail);
        } else {
        	commit("setDeliverablePublishDate", {id:payload.id, value:payload.value});
        }
        commit("increaseRefreshKey");

        /*
        // don't fetch all of the results again, because it will blow away the listing.
        if(myRouteName == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(myRouteName == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(myRouteName == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
        */
    },


    async updateDeliverablePublishStatusBySlug({commit, dispatch}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "updateDeliverablePublishStatusBySlug",
            payload
        );
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);

        /*
        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
        */
    },

    async createSocialCampaign({ commit, getters, state, dispatch }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialCampaign",
            payload
        );

        const socialCampaigns = state.socialCampaigns;
        socialCampaigns.push({id: response.data.campaignDetails.id, title: response.data.campaignDetails.title});
        commit("setAllSocialCampaigns", socialCampaigns);
        commit("setSocialCampaignDetail", response.data.campaignDetails);


        let listSocialCampaigns = state.listSocialCampaigns;
        listSocialCampaigns.push({id: response.data.campaignDetails.id, title: response.data.campaignDetails.title, campaign_id:null, start_date:response.data.campaignDetails.start_date,end_date:response.data.campaignDetails.end_date,total_count:0, list_count:0});

        // resort the campaigns listing by title
         listSocialCampaigns = _.sortBy(listSocialCampaigns,"title");


        commit('setListSocialCampaigns', listSocialCampaigns);
        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
    },

    async createSocialCampaignForDeliverable({ commit, getters, state, dispatch }, payload) {
        const rCampaign = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialCampaign",
            payload
        );

        const socialCampaigns = state.socialCampaigns;
        socialCampaigns.push({id: rCampaign.data.campaignDetails.id, title: rCampaign.data.campaignDetails.title});
        commit("setAllSocialCampaigns", socialCampaigns);
        commit("setSocialCampaignDetail", rCampaign.data.campaignDetails);

        dispatch("setDeliverableSocialCampaign", {
            id: payload.id,
            social_campaign_id: rCampaign.data.campaignDetails.id
        });

        
    },

    async updateSocialCampaign({ commit }, payload) {
        let campaignDetail = {};
        campaignDetail = {
            id: payload.id,
            title: payload.title,
            slug: payload.slug,
            client_id: payload.client_id,
            campaign_id: payload.campaign_id,
            // engagement_manager: payload.engagement_manager,
            // project_lead: payload.project_lead,
            start_date: payload.start_date,
            end_date: payload.end_date
        }

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialCampaign/" + campaignDetail.id,
            campaignDetail
        );


        commit("setSocialCampaign", response.data.socialCampaignDetail);
    },
    async updateSocialCampaignFieldValue({}, fieldUpdateData) {
        let updateData = {};
        if(typeof fieldUpdateData.field_id != 'undefined') {
            let fieldValueData = fieldUpdateData.field_key != 'color_picker' ? fieldUpdateData.value : fieldUpdateData.value.hex;
            updateData = {
                compontent: router.currentRoute.name,
                id: fieldUpdateData.id,
                parentId: fieldUpdateData.parent_id,
                fieldId: fieldUpdateData.field_id,
                socialCampaignFieldId: fieldUpdateData.socialCampaignFieldId,
                value: fieldValueData,
                socialCampaignId: typeof fieldUpdateData.social_campaign_id == 'undefined' ? fieldUpdateData.id : fieldUpdateData.social_campaign_id
            };
        } else {
            updateData = {
                compontent: router.currentRoute.name,
                value: fieldUpdateData.title,
                socialCampaignId:
                    typeof fieldUpdateData.social_campaign_id != "undefined"
                        ? fieldUpdateData.social_campaign_id
                        : fieldUpdateData.id,
                departments: fieldUpdateData.departments,
            };
        }

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialCampaignFieldValue",
            updateData
        );
    },
    async updateSocialSeries({ commit }, payload) {
        let seriesDetail = {};
        seriesDetail = {
            id: payload.id,
            title: payload.title,
            slug: payload.slug,
            client_id: payload.client_id,
            series_id: payload.series_id,
            // engagement_manager: payload.engagement_manager,
            // project_lead: payload.project_lead,
            start_date: payload.start_date,
            end_date: payload.end_date
        }

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialSeries/" + seriesDetail.id,
            seriesDetail
        );

        commit("setAllSocialSeries", response.data.allSocialSeries.data);
        commit("setSocialSeries", response.data.allSocialSeries.data);

    },
    async updateSocialSeriesFieldValue({}, fieldUpdateData) {
        let updateData = {};
        if(typeof fieldUpdateData.field_id != 'undefined') {
        	let fieldValueData = fieldUpdateData.value;
        	if (fieldUpdateData.field_key == 'color_picker') {
        		if (typeof fieldUpdateData.value.hex != "undefined") {
        			fieldValueData = fieldUpdateData.value.hex;
        		}
        	}
            updateData = {
                compontent: router.currentRoute.name,
                id: fieldUpdateData.id,
                parentId: fieldUpdateData.parent_id,
                fieldId: fieldUpdateData.field_id,
                socialSeriesFieldId: fieldUpdateData.socialSeriesFieldId,
                value: fieldValueData,
                socialSeriesId: typeof fieldUpdateData.social_series_id == 'undefined' ? fieldUpdateData.id : fieldUpdateData.social_series_id
            };
        } else {
            updateData = {
                compontent: router.currentRoute.name,
                value: fieldUpdateData.title,
                socialSeriesId:
                    typeof fieldUpdateData.social_series_id != "undefined"
                        ? fieldUpdateData.social_campaign_id
                        : fieldUpdateData.id,
                departments: fieldUpdateData.departments,
            };
        }

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialSeriesFieldValue",
            updateData
        );
    },
    async changeSocialCampaignEngagementManager({}, payload) {
        let data = payload.engagement_manager;
        data.social_campaign_id = payload.social_campaign_id;

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "changeSocialCampaignEngagementManager/" + payload.id,
            data
        );
    },
    async changeSocialCampaignLead({}, payload) {
        let data = payload.project_lead;
        data.social_campaign_id = payload.social_campaign_id;

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "changeSocialCampaignLead/" + payload.id,
            data
        );
    },
    async changeSocialSeriesEngagementManager({}, payload) {
        let data = payload.engagement_manager;
        data.social_series_id = payload.social_series_id;

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "changeSocialSeriesEngagementManager/" + payload.id,
            data
        );
    },
    async changeSocialSeriesLead({}, payload) {
        let data = payload.project_lead;
        data.social_series_id = payload.social_series_id;

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "changeSocialSeriesLead/" + payload.id,
            data
        );
    },
    async fetchSocialMediaCampaignDetail({ commit }, id) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getSocialCampaignDetail/" + id
        );

        commit("setSocialCampaignDetail", response.data.data);
    },
    async setDeliverableSocialCampaign({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "setDeliverableSocialCampaign/",
            payload
        );

        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
    },
    async setDeliverableSocialSeries({ dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "setDeliverableSocialSeries/",
            payload
        );

        // dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
    },

    async setAsManuallyUpdated({commit}, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "setAsManuallyUpdated",
            payload
        );

        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async setAsAutomaticallyUpdated({commit}, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "setAsAutomaticallyUpdated",
            payload
        );

        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);

        // API returns master deliverableTemplate so this doesn't work, need to update PHP function
        // commit("setDeliverable", response.data.deliverableTemplateDetail);
    },

    async createSocialSeriesMultipleDates({commit, dispatch}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialSeriesMultipleDates",
            payload
        );

        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
        commit("setDeliverable", response.data.deliverableTemplateDetail);
        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
    },

    async createSocialSeries({ commit, dispatch }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialSeries",
            payload
        );
        if (payload.from_deliverableTemplate_id) {
    	    commit("replaceDeliverableTemplateDetail", {deliverableTemplateDetail:response.data.deliverableTemplateDetail, id:payload.from_deliverableTemplate_id});
    	}
        commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);


        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverableTemplatesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }
    },

    async addSocialCampaignResourceUrl({ commit, getters }, payload) {
        payload.socialCampaignId = getters.socialCampaignDetail.id;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addSocialCampaignResourceUrl",
            payload
        );

        commit("setSocialCampaignDetail", response.data.socialCampaignDetail);
    },
    async updateSocialCampaignResourceUrl({ commit, getters }, payload) {
        payload.socialCampaignId = getters.socialCampaignDetail.id;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialCampaignResourceUrl",
            payload
        );

        commit("setSocialCampaignDetail", response.data.socialCampaignDetail);
    },
    async removeSocialCampaignResourceUrl({ commit, getters }, payload) {
        payload.socialCampaignId = getters.socialCampaignDetail.id;
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + "removeSocialCampaignResourceUrl/"
                + payload.socialCampaignId + "/" + payload.linkUrl
        );
        commit("setSocialCampaignDetail", response.data.socialCampaignDetail);
    },

    async addSocialSeriesResourceUrl({ commit, getters }, payload) {
        payload.socialSeriesId = getters.socialSeriesDetail.id;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addSocialSeriesResourceUrl",
            payload
        );

        commit("setSocialSeriesDetail", response.data.socialSeriesDetail);
    },
    async updateSocialSeriesResourceUrl({ commit, getters }, payload) {
        payload.socialSeriesId = getters.socialSeriesDetail.id;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialSeriesResourceUrl",
            payload
        );

        commit("setSocialSeriesDetail", response.data.socialSeriesDetail);
    },
    async removeSocialSeriesResourceUrl({ commit, getters }, payload) {
        payload.socialSeriesId = getters.socialSeriesDetail.id;
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + "removeSocialSeriesResourceUrl/"
                + payload.socialSeriesId + "/" + payload.linkUrl
        );
        commit("setSocialSeriesDetail", response.data.socialSeriesDetail);
    },
    async doAction({commit, getters, dispatch}, payload){
        let response = null;
        try{
            response = await axios.put(
                process.env.MIX_API_BASE_URL + "doAction",
                payload
            );
            if (typeof response.data.deliverableTemplateDetail != "undefined") {
	            commit("setDeliverableTemplateDetail", response.data.deliverableTemplateDetail);
	        	commit("setDeliverable", response.data.deliverableTemplateDetail);
	        } else {
	        	 if(typeof response.data.message != 'undefined')
                    toast.error(response.data.message);
                else
                    toast.error('You do not have permission to perform this action.');
                return new Promise((resolve, reject) => {
                    reject("");
                });
	        }
        } catch(e){
            console.error(e);
        }
        
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverableTemplates");
        }

    },

};

export const deliverableTemplates = {
    state,
    mutations,
    getters,
    actions,
    namespaced: true
};